// src/pages/ResidentStatusPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { collection, doc, writeBatch, query, orderBy, getDocs, onSnapshot, serverTimestamp, getDoc, updateDoc, getDocFromServer, collectionGroup, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    FaChevronLeft,
    FaSearch,
    FaUserCheck,
    FaUserTimes,
    FaUsers,
    FaHome,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaCar,
    FaPaw,
    FaCalendarCheck,
    FaCalendarTimes,
    FaUserTie,
    FaCheckCircle,
    FaExclamationTriangle,
    FaUser,
    FaPhoneAlt,
    FaHistory,
    FaFileInvoiceDollar,
    FaMoneyBillWave,
    FaDownload,
    FaEnvelopeOpen,
    FaTimesCircle,
    FaFileAlt,
    FaTint,
    FaDollarSign,
    FaGavel,
    FaCalendarAlt,
    FaChevronDown,
    FaChevronUp,
    FaExclamationCircle,
    FaListUl,
    FaChevronRight,
    FaTimes,
    FaInfoCircle,
    FaChartBar,
} from 'react-icons/fa';
import './DepartamentosDetalle.css';
import ResidentProfileModal from '../components/ResidentProfileModal';
import ModalEdicionSaldos from '../components/ModalEdicionSaldos';
import Swal from 'sweetalert2';
import ModalRegistroPago from '../components/ModalRegistroPago';
import ConfirmationModal from '../components/ConfirmationModal';
import { esPartidaContableResidente } from '../utils/conceptosContables';
import { db, storage, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';
import { calcularAdeudoVisibleDesdeObjeto } from '../utils/calcularAdeudoVisible';
import { calcularEstadoMoroso, esCasaCaseta } from '../utils/morosoUtils';
import { pushToSyncQueue } from '../utils/offlineSyncQueue';
import { useModulos } from '../hooks/useModulos';
import * as XLSX from 'xlsx';

const STATUS_CONFIG = {
    'al-corriente': {
        title: 'Residentes al corriente',
        description: 'Pagos y accesos al día.',
        accentClass: 'ok',
        primaryActions: [
            { label: 'Ver Perfil Completo', variant: 'primary', icon: FaUser },
            { label: 'Contactar', variant: 'info', icon: FaPhoneAlt },
        ],
        secondaryActions: [
            { label: 'Historial', variant: 'ghost', icon: FaHistory },
            { label: 'Generar Ticket, Recibo', variant: 'ghost', icon: FaFileInvoiceDollar },
            { label: 'Mi Documentación', variant: 'ghost', icon: FaFileAlt },
        ],
    },
    morosos: {
        title: 'Residentes morosos',
        description: 'Seguimiento de adeudos y pagos vencidos.',
        accentClass: 'alert',
        primaryActions: [
            { label: 'Ver Perfil Completo', variant: 'primary', icon: FaUser },
            { label: 'Contactar', variant: 'info', icon: FaPhoneAlt },
        ],
        secondaryActions: [
            { label: 'Historial', variant: 'ghost', icon: FaHistory },
            { label: 'Generar Ticket, Recibo', variant: 'ghost', icon: FaFileInvoiceDollar },
            { label: 'Mi Documentación', variant: 'ghost', icon: FaFileAlt },
        ],
    },
};

const MONTH_NAMES_ES = {
    '01': 'Enero', '1': 'Enero', 'ENE': 'Enero', 'ENERO': 'Enero',
    '02': 'Febrero', '2': 'Febrero', 'FEB': 'Febrero', 'FEBRERO': 'Febrero',
    '03': 'Marzo', '3': 'Marzo', 'MAR': 'Marzo', 'MARZO': 'Marzo',
    '04': 'Abril', '4': 'Abril', 'ABR': 'Abril', 'ABRIL': 'Abril',
    '05': 'Mayo', '5': 'Mayo', 'MAY': 'Mayo', 'MAYO': 'Mayo',
    '06': 'Junio', '6': 'Junio', 'JUN': 'Junio', 'JUNIO': 'Junio',
    '07': 'Julio', '7': 'Julio', 'JUL': 'Julio', 'JULIO': 'Julio',
    '08': 'Agosto', '8': 'Agosto', 'AGO': 'Agosto', 'AGOSTO': 'Agosto',
    '09': 'Septiembre', '9': 'Septiembre', 'SEP': 'Septiembre', 'SEPTIEMBRE': 'Septiembre',
    '10': 'Octubre', '10': 'Octubre', 'OCT': 'Octubre', 'OCTUBRE': 'Octubre',
    '11': 'Noviembre', '11': 'Noviembre', 'NOV': 'Noviembre', 'NOVIEMBRE': 'Noviembre',
    '12': 'Diciembre', '12': 'Diciembre', 'DIC': 'Diciembre', 'DICIEMBRE': 'Diciembre'
};

const compararPorNumeroCasa = (a, b) => {
    const numA = String(a?.numero || a?.numeroCasa || a?.lote || '').trim();
    const numB = String(b?.numero || b?.numeroCasa || b?.lote || '').trim();
    return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
};

const formatPeriodoAguaLabel = (mes, anio, periodoLectura, concepto, id) => {
    let mesStr = mes ? String(mes).trim() : '';
    let anioStr = anio ? String(anio).trim() : '';

    if (!mesStr && id && id.startsWith('AGUA-')) {
        const parts = id.split('-');
        if (parts.length >= 4) {
            mesStr = parts[parts.length - 2] || '';
            anioStr = parts[parts.length - 1] || '';
        }
    }

    if (mesStr) {
        const mesKey = mesStr.toUpperCase();
        const mesBonito = MONTH_NAMES_ES[mesKey] || (mesStr.charAt(0).toUpperCase() + mesStr.slice(1).toLowerCase());
        return anioStr ? `${mesBonito} ${anioStr}` : mesBonito;
    }

    if (periodoLectura) return periodoLectura;
    if (concepto) return concepto;
    return 'Consumo de Agua';
};

const VEHICLE_COUNT_KEYS = [
    'vehiculos',
    'vehiculosRegistrados',
    'totalVehiculos',
    'vehiculosTotal',
    'numeroVehiculos',
    'vehiculos_count',
    'vehiculosCantidad',
    'num_vehiculos',
    'numero_vehiculos',
    'cantidad_vehiculos',
    'numVehiculos',
    'total_de_vehiculos',
    'vehiculosRegistradosTotal',
    'vehiculosPropios',
    'totalVehiculo',
    'numvehiculos',
];

const PET_DATA_KEYS = [
    'mascotas',
    'mascotasRegistradas',
    'totalMascotas',
    'mascotasTotal',
    'numeroMascotas',
    'mascotasDetalle',
    'mascotas_count',
];

const searchNestedValue = (source, keys, depth = 0) => {
    if (!source || typeof source !== 'object' || depth > 3) {
        return undefined;
    }

    for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const value = source[key];
            if (value !== undefined && value !== null) {
                if (typeof value === 'string') {
                    const trimmed = value.trim();
                    if (trimmed.length > 0) {
                        return trimmed;
                    }
                } else {
                    return value;
                }
            }
        }
    }

    for (const value of Object.values(source)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = searchNestedValue(value, keys, depth + 1);
            if (nested !== undefined) {
                return nested;
            }
        }
    }

    return undefined;
};

const pickFirstValue = (source, keys) => {
    if (!source || typeof source !== 'object') {
        return undefined;
    }
    for (const key of keys) {
        if (!Object.prototype.hasOwnProperty.call(source, key)) {
            continue;
        }
        const value = source[key];
        if (value === undefined || value === null) {
            continue;
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.length === 0) {
                continue;
            }
            return trimmed;
        }
        return value;
    }
    return undefined;
};

const searchVehicleLikeFallback = (source, depth = 0) => {
    if (!source || typeof source !== 'object' || depth > 3) {
        return undefined;
    }

    const matchedEntry = Object.entries(source).find(([key, value]) => {
        if (!key || typeof key !== 'string') {
            return false;
        }
        const normalizedKey = key.toLowerCase();
        if (!normalizedKey.includes('veh')) {
            return false;
        }
        if (value === undefined || value === null) {
            return false;
        }
        if (typeof value === 'number') {
            return true;
        }
        if (typeof value === 'string') {
            return value.trim().length > 0;
        }
        return false;
    });

    if (matchedEntry) {
        const value = matchedEntry[1];
        return typeof value === 'string' ? value.trim() : value;
    }

    for (const value of Object.values(source)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = searchVehicleLikeFallback(value, depth + 1);
            if (nested !== undefined) {
                return nested;
            }
        }
    }

    return undefined;
};

const pickVehicleMetric = (resident) => {
    const direct = pickFirstValue(resident, VEHICLE_COUNT_KEYS);
    if (direct !== undefined) {
        return direct;
    }

    const nested = searchNestedValue(resident, VEHICLE_COUNT_KEYS);
    if (nested !== undefined) {
        return nested;
    }

    return searchVehicleLikeFallback(resident);
};

const toNonNegativeInteger = (value) => {
    if (value === undefined || value === null) {
        return null;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) {
        return null;
    }
    return Math.floor(numeric);
};

const buildMascotasLabel = (rawMascotas) => {
    if (rawMascotas === undefined || rawMascotas === null) {
        return null;
    }
    if (Array.isArray(rawMascotas)) {
        if (rawMascotas.length === 0) {
            return 'Sin mascotas';
        }
        return `${rawMascotas.length} mascota${rawMascotas.length === 1 ? '' : 's'} `;
    }
    const numeric = toNonNegativeInteger(rawMascotas);
    if (numeric !== null) {
        return numeric === 0 ? 'Sin mascotas' : `${numeric} mascota${numeric === 1 ? '' : 's'} `;
    }
    if (typeof rawMascotas === 'string') {
        const trimmed = rawMascotas.trim();
        return trimmed.length > 0 ? trimmed : 'Sin mascotas';
    }
    return null;
};

const toJsDate = (value) => {
    if (!value && value !== 0) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'number') {
        if (value > 10_000 && value < 50_000) {
            const excelEpoch = new Date(Date.UTC(1899, 11, 30));
            const result = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
            return Number.isNaN(result.getTime()) ? null : result;
        }
        const result = new Date(value);
        return Number.isNaN(result.getTime()) ? null : result;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }
        const parsed = Date.parse(trimmed);
        if (!Number.isNaN(parsed)) {
            return new Date(parsed);
        }
        const parts = trimmed.split(/[/-]/);
        if (parts.length === 3) {
            const [part1, part2, part3] = parts.map((segment) => segment.replace(/[^0-9]/g, ''));
            if (part1.length && part2.length && part3.length) {
                const day = Number(part1.length === 4 ? part3 : part1);
                const month = Number(part2) - 1;
                const year = Number(part1.length === 4 ? part1 : part3);
                const candidate = new Date(year, month, day);
                if (!Number.isNaN(candidate.getTime())) {
                    return candidate;
                }
            }
        }
        return null;
    }
    if (value && typeof value.toDate === 'function') {
        try {
            const date = value.toDate();
            return Number.isNaN(date.getTime()) ? null : date;
        } catch (error) {
            return null;
        }
    }
    if (value && typeof value === 'object') {
        const { seconds, nanoseconds } = value;
        if (typeof seconds === 'number' && typeof nanoseconds === 'number') {
            const millis = seconds * 1000 + Math.floor(nanoseconds / 1_000_000);
            const date = new Date(millis);
            return Number.isNaN(date.getTime()) ? null : date;
        }
    }
    return null;
};

const formatDateLabel = (date) => {
    if (!date) {
        return null;
    }
    const formatter = new Intl.DateTimeFormat('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
    const formatted = formatter.format(date);
    return formatted.replace(/\b([a-záéíóúñ])/g, (match) => match.toUpperCase());
};

const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(amount) || 0);

const computeDelayDays = (resident) => {
    const rawDelay = pickFirstValue(resident, [
        'diasRetraso',
        'dias_retraso',
        'diasMora',
        'dias_mora',
        'diasAtraso',
        'dias_atraso',
    ]);
    const parsedDelay = toNonNegativeInteger(rawDelay);
    if (parsedDelay && parsedDelay > 0) {
        return parsedDelay;
    }

    const dueCandidate = pickFirstValue(resident, [
        'fechaProximoPago',
        'proximoPago',
        'proximo_pago',
        'proximoPagoFecha',
        'fecha_proximo_pago',
        'nextPaymentDate',
        'fechaProximoPagoISO',
        'fecha_limite_pago',
        'fechaLimitePago',
        'fechaVencimiento',
        'vencimiento',
        'fechaCorte',
    ]);
    const dueDate = toJsDate(dueCandidate);
    if (dueDate && dueDate.getTime() < Date.now()) {
        const diff = Math.floor((Date.now() - dueDate.getTime()) / (24 * 60 * 60 * 1000));
        return diff > 0 ? diff : 0;
    }
    return 0;
};

const computeNextCutoffDateFromDay = (cutoffDay, referenceDate = new Date()) => {
    const numeric = Number(cutoffDay);
    if (!Number.isFinite(numeric)) {
        return null;
    }
    const baseDay = Math.min(Math.max(Math.floor(numeric), 1), 31);

    const currentYear = referenceDate.getFullYear();
    const currentMonth = referenceDate.getMonth();

    const clampDay = (year, month) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        return Math.min(baseDay, daysInMonth);
    };

    const candidateDay = clampDay(currentYear, currentMonth);
    const candidate = new Date(currentYear, currentMonth, candidateDay);

    if (candidate.getTime() > referenceDate.getTime()) {
        return candidate;
    }

    const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
    const nextDay = clampDay(nextMonthDate.getFullYear(), nextMonthDate.getMonth());
    return new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), nextDay);
};

const ResidentStatusPage = () => {
    const { statusType = 'al-corriente' } = useParams();
    const location = useLocation();
    const normalizedStatus = STATUS_CONFIG[statusType] ? statusType : 'al-corriente';
    const config = STATUS_CONFIG[normalizedStatus];
    const isMorosoView = normalizedStatus === 'morosos';

    const [residents, setResidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState(() => {
        return location.state?.search || '';
    });
    const [billingCutoffDay, setBillingCutoffDay] = useState(null);
    const [selectedResident, setSelectedResident] = useState(null);
    const [paymentResident, setPaymentResident] = useState(null);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [historyResident, setHistoryResident] = useState(null);
    const [rawIngresos, setRawIngresos] = useState([]);
    const [rawHistorial, setRawHistorial] = useState([]);
    const [rawExtrasPendientes, setRawExtrasPendientes] = useState([]);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [historyUnsubscribe, setHistoryUnsubscribe] = useState(null);
    const [selectedIngreso, setSelectedIngreso] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [sendingStatus, setSendingStatus] = useState(null);
    const [historySearchTerm, setHistorySearchTerm] = useState('');
    const [historyPage, setHistoryPage] = useState(0);
    const [recibosConfig, setRecibosConfig] = useState(null);
    const [chargesState, setChargesState] = useState(null);
    const [applyingCharges, setApplyingCharges] = useState(false);
    const [applyChargesMessage, setApplyChargesMessage] = useState(null);
    const [financialConfig, setFinancialConfig] = useState(null);
    const [confirmationModal, setConfirmationModal] = useState({
        open: false,
        title: '',
        message: '',
        type: 'info',
    });
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    // Estados para el modal de Edición de Saldos en ResidentStatusPage
    const [showSaldosModal, setShowSaldosModal] = useState(false);
    const [saldosCasaData, setSaldosCasaData] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [expandedWaterCards, setExpandedWaterCards] = useState({});
    const [mapMultasPendientes, setMapMultasPendientes] = useState({});
    const [mapMultasDetalle, setMapMultasDetalle] = useState({});
    const [mapExtrasPendientes, setMapExtrasPendientes] = useState({});

    const { fraccionamientoId, userData, currentUser } = useAuth();
    const { modulos } = useModulos(fraccionamientoId);
    const navigate = useNavigate();

    // Estados para el Modal / Bottom Sheet de Desglose de Adeudos (Mes con Mes y Año por Año)
    const [desgloseModalData, setDesgloseModalData] = useState({
        isOpen: false,
        resident: null,
        tab: 'mantenimiento', // 'mantenimiento' | 'extraordinarias' | 'sanciones'
        loading: false,
        mesesData: [],
        multasData: [],
        aniosExpandidos: {}
    });

    const handleOpenDesgloseModal = useCallback(async (resident, initialTab = 'mantenimiento') => {
        if (!resident || !resident.id || !fraccionamientoId) return;

        const idN = String(resident.id || '').trim().toUpperCase();
        const numN = String(resident.numero || resident.numeroCasa || '').trim().toUpperCase();
        const multasPrecargadas = mapMultasDetalle[idN] || mapMultasDetalle[numN] || [];

        setDesgloseModalData({
            isOpen: true,
            resident,
            tab: initialTab,
            loading: true,
            mesesData: [],
            multasData: multasPrecargadas,
            aniosExpandidos: {}
        });

        try {
            const mesesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', resident.id, 'mesesPendientes');
            const snap = await getDocs(mesesRef);
            const data = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

            let multas = multasPrecargadas;
            if (multas.length === 0) {
                try {
                    const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
                    const snapMultas = await getDocs(multasRef);
                    multas = snapMultas.docs
                        .map(d => ({ id: d.id, ...d.data() }))
                        .filter(m => !m.pagada && (
                            String(m.casaId || '').trim().toUpperCase() === idN ||
                            String(m.casaNumero || m.numeroCasa || '').trim().toUpperCase() === numN
                        ));
                } catch (errM) {
                    console.warn('ResidentStatusPage: error cargando multas para desglose:', errM);
                }
            }

            setDesgloseModalData(prev => ({
                ...prev,
                loading: false,
                mesesData: data,
                multasData: multas
            }));
        } catch (err) {
            console.error('Error cargando mesesPendientes para desglose administrador:', err);
            setDesgloseModalData(prev => ({
                ...prev,
                loading: false,
                multasData: multasPrecargadas
            }));
        }
    }, [fraccionamientoId, mapMultasDetalle]);

    const desgloseMantenimientoAgrupado = useMemo(() => {
        if (!desgloseModalData.mesesData || desgloseModalData.mesesData.length === 0) return [];
        const mesesRegulares = desgloseModalData.mesesData.filter(m => {
            const isExtra = m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria' || m.esExtraordinaria;
            const isAgua = m.id?.startsWith('AGUA-') || m.tipo === 'adeudo_agua' || m.tipo === 'consumo_agua';
            const est = String(m.estado || '').toLowerCase().trim();
            const esPagado = ['pagado_completo', 'pagado', 'pagado_totalmente'].includes(est);
            const montoPendiente = Number(m.montoPendiente ?? m.monto ?? 0);
            return !isExtra && !isAgua && !esPagado && montoPendiente > 0;
        });

        const mesesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const grupos = {};

        mesesRegulares.forEach(m => {
            let anio = Number(m.anio || m.año);
            let mesNum = Number(m.mes || m.mesNumero);

            if (!anio && m.id && m.id.includes('-')) {
                const parts = m.id.replace('his-', '').split('-');
                anio = Number(parts[0]);
                mesNum = Number(parts[1]);
            }
            if (!anio) anio = 2026;

            if (!grupos[anio]) {
                grupos[anio] = {
                    anio,
                    totalMontoPendiente: 0,
                    totalMontoOriginal: 0,
                    totalMontoPagado: 0,
                    meses: []
                };
            }

            const montoOriginal = Number(m.montoOriginal ?? m.cuotaMensual ?? m.monto ?? 0);
            const montoPendiente = Number(m.montoPendiente ?? m.monto ?? 0);
            const montoPagado = Number(m.montoPagado ?? 0);
            const mesNombre = m.mesNombre || mesesNombres[(mesNum ? mesNum - 1 : 0) % 12];

            grupos[anio].totalMontoPendiente += montoPendiente;
            grupos[anio].totalMontoOriginal += montoOriginal;
            grupos[anio].totalMontoPagado += montoPagado;
            grupos[anio].meses.push({
                id: m.id,
                mesNumero: mesNum,
                mesNombre: mesNombre.charAt(0).toUpperCase() + mesNombre.slice(1),
                montoOriginal,
                montoPagado,
                montoPendiente,
                estado: m.estado || 'pendiente',
                observaciones: m.observaciones || ''
            });
        });

        return Object.values(grupos).sort((a, b) => a.anio - b.anio);
    }, [desgloseModalData.mesesData]);

    const desgloseCuotasExtrasDetalladas = useMemo(() => {
        if (!desgloseModalData.mesesData || desgloseModalData.mesesData.length === 0) return [];
        return desgloseModalData.mesesData
            .filter(m => {
                const isExtra = m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria' || m.esExtraordinaria;
                const est = String(m.estado || '').toLowerCase().trim();
                const esPagado = ['pagado_completo', 'pagado', 'pagado_totalmente'].includes(est);
                const montoPendiente = Number(m.montoPendiente ?? m.monto ?? 0);
                return isExtra && !esPagado && montoPendiente > 0;
            })
            .map(m => {
                let concepto = m.concepto || m.descripcion || m.id || 'Cuota Extraordinaria';
                let anio = Number(m.anio || m.año || 0);
                if (!anio && m.id) {
                    const matchAnio = m.id.match(/\b(20\d\d)\b/);
                    if (matchAnio) anio = Number(matchAnio[1]);
                }
                const montoOriginal = Number(m.montoOriginal ?? m.monto ?? 0);
                const montoPendiente = Number(m.montoPendiente ?? m.monto ?? 0);
                const montoPagado = Number(m.montoPagado || 0);

                return {
                    id: m.id,
                    concepto,
                    anio: anio || null,
                    montoOriginal,
                    montoPagado,
                    montoPendiente,
                    estado: m.estado || 'pendiente'
                };
            })
            .sort((a, b) => (a.anio || 9999) - (b.anio || 9999));
    }, [desgloseModalData.mesesData]);

    const handleOpenEditSaldosModal = async (resident) => {
        if (!resident || !resident.id || !fraccionamientoId) return;
        
        try {
            const { doc, getDoc } = await import('firebase/firestore');
            const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', resident.id);
            const casaSnap = await getDoc(casaRef);
            if (casaSnap.exists()) {
                const data = casaSnap.data() || {};
                setSaldosCasaData({
                    id: resident.id,
                    numero: resident.numero || data.numero || '',
                    propietario: resident.nombre || data.propietario || data.nombre_residente || 'Sin residente asignado',
                    adeudo: Number(data.adeudo || 0),
                    adeudoCapital: Number(data.adeudoCapital || 0),
                    adeudoIntereses: Number(data.adeudoIntereses || 0),
                    saldoFavor: Number(data.saldoFavor || 0)
                });
                setShowSaldosModal(true);
            } else {
                Swal.fire({
                    title: 'Aviso',
                    text: 'No se pudo encontrar el registro de esta casa en la base de datos.',
                    icon: 'warning',
                    confirmButtonColor: '#e2e8f0',
                    confirmButtonText: 'Aceptar'
                });
            }
        } catch (error) {
            console.error('Error al obtener datos de la casa para edición de saldos:', error);
            Swal.fire({
                title: 'Error de Red',
                text: 'No se pudo conectar con el servidor para leer la información.',
                icon: 'error',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'Cerrar'
            });
        }
    };



    const todayKey = useMemo(() => new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'America/Mexico_City',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date()), []);

    const historyIngresos = useMemo(() => {
        const idsExtrasProcesadas = new Set();

        // Consolidar ingresos (activos) y historial (importados)
        const rawIngresosFiltrados = rawIngresos.filter(ing => ing.tipo !== 'adeudo_historico' && !ing.deleted);
        const rawHistorialFiltrado = rawHistorial.filter(ing => ing.tipo !== 'adeudo_historico' && !ing.deleted);
        const todosLosMovimientos = [...rawIngresosFiltrados, ...rawHistorialFiltrado];

        todosLosMovimientos.forEach(ing => {
            if (ing.cuotasExtraniordinarias && Array.isArray(ing.cuotasExtraniordinarias)) {
                ing.cuotasExtraniordinarias.forEach(c => {
                    if (c.id) idsExtrasProcesadas.add(c.id);
                });
            }
        });

        // Deduplicar exclusivamente cargos (adeudos automáticos) de cuota extraordinaria con la misma referencia
        const referenciasVistas = new Set();
        const ingresosDeduplicados = [];

        todosLosMovimientos.forEach(ing => {
            const ref = ing.referencia || ing.id;
            const concepto = (ing.concepto || '').toLowerCase();
            const metodoPago = (ing.metodoPago || '').toLowerCase();
            const tipoLower = (ing.tipo || '').toLowerCase();
            
            const esAdeudoCargo = tipoLower.includes('adeudo') || concepto.includes('adeudo') || metodoPago === 'registro_automatico';
            const esExtraordinaria = tipoLower.includes('extraordinaria') || 
                                     concepto.includes('extraordinaria') || 
                                     concepto.includes('cuota extra') || 
                                     (ref && (ref.startsWith('EXT-') || ref.includes('EXT')));
            
            if (esAdeudoCargo && esExtraordinaria && ref) {
                if (!referenciasVistas.has(ref)) {
                    referenciasVistas.add(ref);
                    ingresosDeduplicados.push(ing);
                } else {
                    console.log('Deduplicado cargo extraordinario repetido con referencia:', ref);
                }
            } else {
                ingresosDeduplicados.push(ing);
            }
        });

        const extrasMapeadas = rawExtrasPendientes
            .filter(doc => doc.estado !== 'pagado_completo' && (doc.id.startsWith('EXT-') || doc.tipo === 'cuota_extraordinaria'))
            .map(doc => {
                const data = doc;
                const timestampObj = data.fechaCorte || data.fechaCreacion || data.fechaVencimiento || null;
                
                let conceptoExtra = data.concepto || data.descripcion || 'Cuota Extraordinaria';
                if (!conceptoExtra.toLowerCase().includes('adeudo') && !conceptoExtra.toLowerCase().includes('cargo')) {
                    conceptoExtra = 'Adeudo Cuota Extraordinaria: ' + conceptoExtra;
                }
                
                return {
                    id: doc.id,
                    casaId: doc.casaId || historyResident?.id || '',
                    concepto: conceptoExtra,
                    tipo: 'extraordinaria',
                    metodoPago: 'registro_automatico',
                    referencia: doc.id,
                    monto: data.montoOriginal || data.totalMensual || data.monto || 0,
                    descuento: 0,
                    montoNeto: -Math.abs(data.montoPendiente || data.monto || 0),
                    fecha: timestampObj,
                    timestamp: timestampObj,
                    estadoPago: 'moroso',
                    estadoResidente: 'CON ADEUDO',
                    colorBandera: 'rojo',
                    esRegistroMensual: false,
                    esExtraPendiente: true
                };
            })
            .filter(item => !idsExtrasProcesadas.has(item.id) && !referenciasVistas.has(item.id));

        const resultadoFinal = [...ingresosDeduplicados];

        // Ordenar cronológicamente de forma descendente usando timestamp o fecha
        resultadoFinal.sort((a, b) => {
            const dateA = (a.timestamp?.toDate ? a.timestamp.toDate() : (a.timestamp ? new Date(a.timestamp) : null))
                || (a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha || 0));
            const dateB = (b.timestamp?.toDate ? b.timestamp.toDate() : (b.timestamp ? new Date(b.timestamp) : null))
                || (b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha || 0));
            return dateB - dateA;
        });

        return resultadoFinal;
    }, [rawIngresos, rawHistorial, rawExtrasPendientes, historyResident]);

    const filteredHistory = useMemo(() => {
        const list = historyIngresos.filter(ing =>
            (ing.concepto || '').toLowerCase().includes(historySearchTerm.toLowerCase()) ||
            (ing.referencia || '').toLowerCase().includes(historySearchTerm.toLowerCase()) ||
            (ing.metodoPago || '').toLowerCase().includes(historySearchTerm.toLowerCase())
        );
        list.sort((a, b) => {
            const da = (a.timestamp?.toDate ? a.timestamp.toDate() : (a.timestamp ? new Date(a.timestamp) : null))
                || (a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha || 0));
            const db = (b.timestamp?.toDate ? b.timestamp.toDate() : (b.timestamp ? new Date(b.timestamp) : null))
                || (b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha || 0));
            
            const diff = db - da;
            if (diff !== 0) return diff; // más reciente primero

            const getPriority = (item) => {
                if (item.esRegistroMensual) return 3; // Mantenimiento y Saldo Final
                const conceptoLower = (item.concepto || '').toLowerCase();
                const esCargoAdeudo = item.esExtraPendiente === true || 
                                      (conceptoLower.includes('adeudo') && item.metodoPago === 'registro_automatico');
                if (esCargoAdeudo) return 1;  // Adeudo Extraordinaria (al final)
                return 2;                     // Pagos de extraordinaria o saldo_favor
            };
            return getPriority(b) - getPriority(a);
        });
        return list;
    }, [historyIngresos, historySearchTerm]);

    const totalHistoryPages = Math.max(1, Math.ceil(filteredHistory.length / 9));
    const currentHistoryPage = Math.min(Math.max(historyPage || 0, 0), totalHistoryPages - 1);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (!fraccionamientoId) {
            setResidents([]);
            setLoading(false);
            setError('');
            return;
        }

        setLoading(true);
        setError('');

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const casasQuery = query(casasRef, orderBy('numero'));
        const unsubscribe = onSnapshot(casasQuery, (snapshot) => {
            const lista = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
            lista.sort(compararPorNumeroCasa);
            setResidents(lista);
            setLoading(false);
        }, (err) => {
            console.error('ResidentStatusPage: error al cargar residentes', err);
            setResidents([]);
            setLoading(false);
            setError('No se pudo cargar la información de residentes.');
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    const openResidentHistory = useCallback(async (resident) => {
        if (!resident || !fraccionamientoId) return;
        try {
            const casaId = resident.id;
            const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
            const q = query(
                ingresosRef,
                orderBy('fecha', 'desc')
            );

            const unsubscribeIngresos = onSnapshot(q, (snapshot) => {
                const ingresos = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(ing => ing.casaId === casaId);

                setRawIngresos(ingresos);
                setHistoryResident(resident);
                console.log('openResidentHistory: ingresos actualizados en tiempo real:', ingresos.length);
            }, (error) => {
                console.error('Error en listener de ingresos:', error);
            });

            const historialRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId, 'historial_pagos');
            const qHistorial = query(historialRef, orderBy('fecha', 'desc'));
            const unsubscribeHistorial = onSnapshot(qHistorial, (snapshot) => {
                const historial = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRawHistorial(historial);
                console.log('openResidentHistory: historial_pagos actualizado en tiempo real:', historial.length);
            }, (error) => {
                console.error('Error en listener de historial_pagos:', error);
            });

            const mesesPendientesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId, 'mesesPendientes');
            const unsubscribeMesesPendientes = onSnapshot(mesesPendientesRef, (snapshot) => {
                const extras = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRawExtrasPendientes(extras);
                console.log('openResidentHistory: meses pendientes actualizados en tiempo real:', extras.length);
            }, (error) => {
                console.error('Error en listener de meses pendientes:', error);
            });

            setHistoryUnsubscribe(prev => {
                if (prev) {
                    try { prev(); } catch(e) { console.error('Error al cancelar suscripción anterior:', e); }
                }
                return () => {
                    unsubscribeIngresos();
                    unsubscribeHistorial();
                    unsubscribeMesesPendientes();
                };
            });
            setIsHistoryModalOpen(true);
        } catch (error) {
            console.error('Error abriendo historial de residente:', error);
        }
    }, [fraccionamientoId]);

    useEffect(() => {
        if (residents.length > 0) {
            if (location.state?.selectedResidentId) {
                const residentToOpen = residents.find(r => r.id === location.state.selectedResidentId);
                if (residentToOpen) {
                    if (location.state.openHistory) {
                        openResidentHistory(residentToOpen);
                    } else if (location.state.openTicket) {
                        setPaymentResident(residentToOpen);
                        setIsPaymentModalOpen(true);
                    } else if (location.state.openProfile) {
                        setSelectedResident(residentToOpen);
                    }
                    // Limpiar el estado de navegación para evitar reaperturas accidentales
                    window.history.replaceState({}, document.title);
                }
            }
        }
    }, [location.state, residents, openResidentHistory]);

    const [mapAguaPendiente, setMapAguaPendiente] = useState({});
    const [mapPeriodosAguaPendientes, setMapPeriodosAguaPendientes] = useState({});

    useEffect(() => {
        if (!fraccionamientoId) return;
        const consumosAguaRef = collection(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable');
        const unsubscribe = onSnapshot(consumosAguaRef, (snapshot) => {
            const mapAdeudo = {};
            const mapPeriodosAgua = {};
            snapshot.docs.forEach(docAgua => {
                const dataAgua = docAgua.data();
                const desglose = dataAgua.desgloseCasas || [];
                desglose.forEach(item => {
                    const est = String(item.estadoPago || item.estado || '').toUpperCase();
                    const isPendiente = item.pagado === false || (item.pagado !== true && est !== 'PAGADO');
                    if (isPendiente) {
                        const cId = String(item.casaId || '').trim().toUpperCase();
                        const cNum = String(item.casaNumero || '').trim().toUpperCase();
                        const monto = Number(item.cargoTotal || item.montoPendiente || item.monto || 0);
                        if (cId) {
                            mapAdeudo[cId] = (mapAdeudo[cId] || 0) + monto;
                        }
                        if (cNum) {
                            mapAdeudo[cNum] = (mapAdeudo[cNum] || 0) + monto;
                        }
                        if (monto > 0) {
                            const label = formatPeriodoAguaLabel(dataAgua.mesLectura, dataAgua.anoLectura, dataAgua.periodoLectura, null, docAgua.id);
                            const periodObj = {
                                id: docAgua.id,
                                key: `${dataAgua.mesLectura || ''}-${dataAgua.anoLectura || ''}`.toUpperCase(),
                                periodo: label,
                                monto,
                                consumoM3: item.consumoM3 || 0
                            };
                            if (cId) {
                                if (!mapPeriodosAgua[cId]) mapPeriodosAgua[cId] = [];
                                if (!mapPeriodosAgua[cId].some(p => p.key === periodObj.key || p.id === periodObj.id || p.periodo === periodObj.periodo)) {
                                    mapPeriodosAgua[cId].push(periodObj);
                                }
                            }
                            if (cNum) {
                                if (!mapPeriodosAgua[cNum]) mapPeriodosAgua[cNum] = [];
                                if (!mapPeriodosAgua[cNum].some(p => p.key === periodObj.key || p.id === periodObj.id || p.periodo === periodObj.periodo)) {
                                    mapPeriodosAgua[cNum].push(periodObj);
                                }
                            }
                        }
                    }
                });
            });
            setMapAguaPendiente(mapAdeudo);
            setMapPeriodosAguaPendientes(prev => {
                const next = { ...prev };
                Object.entries(mapPeriodosAgua).forEach(([k, list]) => {
                    next[k] = list;
                });
                return next;
            });
        }, (err) => {
            console.warn('ResidentStatusPage: error escuchando consumosAguaPotable', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Escuchar todas las multas administrativas no pagadas para mapear los adeudos y detalles por casaId y casaNumero
    useEffect(() => {
        if (!fraccionamientoId) {
            setMapMultasPendientes({});
            setMapMultasDetalle({});
            return;
        }
        const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
        const unsubscribe = onSnapshot(multasRef, (snapshot) => {
            const mapMultas = {};
            const mapDetalle = {};
            snapshot.docs.forEach(docMulta => {
                const dataMulta = docMulta.data() || {};
                const pagada = dataMulta.pagada === true;
                if (!pagada) {
                    const cId = String(dataMulta.casaId || '').trim().toUpperCase();
                    const cNum = String(dataMulta.casaNumero || dataMulta.numeroCasa || dataMulta.numero || '').trim().toUpperCase();
                    const monto = Number(dataMulta.monto || 0);
                    const item = { id: docMulta.id, ...dataMulta, monto };

                    if (cId) {
                        mapMultas[cId] = (mapMultas[cId] || 0) + monto;
                        if (!mapDetalle[cId]) mapDetalle[cId] = [];
                        mapDetalle[cId].push(item);
                    }
                    if (cNum && cNum !== cId) {
                        mapMultas[cNum] = (mapMultas[cNum] || 0) + monto;
                        if (!mapDetalle[cNum]) mapDetalle[cNum] = [];
                        mapDetalle[cNum].push(item);
                    }
                }
            });
            setMapMultasPendientes(mapMultas);
            setMapMultasDetalle(mapDetalle);
        }, (err) => {
            console.warn('ResidentStatusPage: error escuchando multasAdministrativas', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Escuchar todos los meses pendientes (cuotas extraordinarias y agua) para mapear los adeudos por casaId
    useEffect(() => {
        if (!fraccionamientoId) {
            setMapExtrasPendientes({});
            return;
        }
        const q = query(
            collectionGroup(db, 'mesesPendientes'),
            where('fraccionamientoId', '==', String(fraccionamientoId))
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const mapExtras = {};
            const mapAguaFromMeses = {};
            const mapPeriodosFromMeses = {};
            snapshot.docs.forEach(docMes => {
                const data = docMes.data() || {};
                const mesId = docMes.id;
                const est = String(data.estado || '').toLowerCase();
                const esPagada = est === 'pagado_completo' || est === 'pagado_totalmente' || est === 'pagado';
                const esExtraordinaria = mesId.startsWith('EXT-') || data.tipo === 'cuota_extraordinaria';
                const esAguaDoc = mesId.startsWith('AGUA-') || data.tipo === 'adeudo_agua';
                
                if (!esPagada && esExtraordinaria) {
                    const casaId = String(data.casaId || docMes.ref.parent?.parent?.id || '').trim().toUpperCase();
                    const monto = Number(data.montoPendiente ?? data.monto ?? 0);
                    if (casaId) {
                        mapExtras[casaId] = (mapExtras[casaId] || 0) + monto;
                    }
                }

                if (!esPagada && esAguaDoc) {
                    const casaId = String(data.casaId || docMes.ref.parent?.parent?.id || '').trim().toUpperCase();
                    const casaNum = String(data.casaNumero || '').trim().toUpperCase();
                    const monto = Number(data.montoPendiente ?? data.monto ?? 0);
                    if (casaId) mapAguaFromMeses[casaId] = (mapAguaFromMeses[casaId] || 0) + monto;
                    if (casaNum) mapAguaFromMeses[casaNum] = (mapAguaFromMeses[casaNum] || 0) + monto;

                    if (monto > 0) {
                        const label = formatPeriodoAguaLabel(data.mes || data.mesLectura, data.año || data.anio || data.anoLectura, data.periodoLectura, data.concepto || data.descripcion, mesId);
                        const periodKey = (mesId.startsWith('AGUA-') ? mesId.split('-').slice(-2).join('-') : `${data.mes || ''}-${data.año || data.anio || ''}`).toUpperCase();
                        const periodObj = {
                            id: mesId,
                            key: periodKey,
                            periodo: label,
                            monto,
                            consumoM3: data.consumoM3 || 0
                        };
                        if (casaId) {
                            if (!mapPeriodosFromMeses[casaId]) mapPeriodosFromMeses[casaId] = [];
                            if (!mapPeriodosFromMeses[casaId].some(p => (periodObj.key && p.key === periodObj.key) || p.id === periodObj.id || p.periodo === periodObj.periodo)) {
                                mapPeriodosFromMeses[casaId].push(periodObj);
                            }
                        }
                        if (casaNum) {
                            if (!mapPeriodosFromMeses[casaNum]) mapPeriodosFromMeses[casaNum] = [];
                            if (!mapPeriodosFromMeses[casaNum].some(p => (periodObj.key && p.key === periodObj.key) || p.id === periodObj.id || p.periodo === periodObj.periodo)) {
                                mapPeriodosFromMeses[casaNum].push(periodObj);
                            }
                        }
                    }
                }
            });
            setMapExtrasPendientes(mapExtras);
            if (Object.keys(mapAguaFromMeses).length > 0) {
                setMapAguaPendiente(prev => ({
                    ...prev,
                    ...mapAguaFromMeses
                }));
            }
            if (Object.keys(mapPeriodosFromMeses).length > 0) {
                setMapPeriodosAguaPendientes(prev => {
                    const next = { ...prev };
                    Object.entries(mapPeriodosFromMeses).forEach(([cKey, list]) => {
                        if (!next[cKey]) {
                            next[cKey] = list;
                        } else {
                            const currentList = [...next[cKey]];
                            list.forEach(item => {
                                const exists = currentList.some(p => 
                                    (item.key && p.key === item.key) || 
                                    p.id === item.id || 
                                    p.periodo === item.periodo
                                );
                                if (!exists) {
                                    currentList.push(item);
                                }
                            });
                            next[cKey] = currentList;
                        }
                    });
                    return next;
                });
            }
        }, (err) => {
            console.warn('ResidentStatusPage: error escuchando mesesPendientes collectionGroup', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setBillingCutoffDay(null);
            setRecibosConfig(null);
            return;
        }

        const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsubscribe = onSnapshot(fraccRef, (snapshot) => {
            if (!snapshot.exists()) {
                setBillingCutoffDay(null);
                setRecibosConfig(null);
                return;
            }
            const data = snapshot.data() || {};
            const configuracion = data.configuracionFinanciera || data.finanzasConfiguracion || {};
            const diaConfig = pickFirstValue(configuracion, [
                'corteDia',
                'diaCorte',
                'dia_corte',
                'cutOffDay',
            ]);
            if (diaConfig !== undefined && diaConfig !== null && diaConfig !== '') {
                const parsed = Number(diaConfig);
                setBillingCutoffDay(Number.isFinite(parsed) ? parsed : null);
            } else {
                setBillingCutoffDay(null);
            }
            // Fetch recibos configuration
            setRecibosConfig(data.configuracionRecibos || {});
            // Normalizar campos para período de gracia
            const normalizedConfig = {
                ...configuracion,
                mesesPermitidos: configuracion?.interesesMesesPermitidosDeuda ?? configuracion?.mesesPermitidos ?? configuracion?.meses_permitidos ?? 1,
                cuotaMensual: configuracion?.cuotaMensual ?? configuracion?.montoMensual ?? configuracion?.cuota_mensual ?? 0,
                sancionesInicioDia: configuracion?.sancionesInicioDia ?? configuracion?.corteDia ?? 1,
            };
            setFinancialConfig(normalizedConfig);
            console.log('✅ ResidentStatusPage: configuración financiera cargada ->', {
                mesesPermitidos: normalizedConfig.mesesPermitidos,
                cuotaMensual: normalizedConfig.cuotaMensual,
                campo_original: configuracion.interesesMesesPermitidosDeuda
            });
        }, (err) => {
            console.error('ResidentStatusPage: error al leer configuración financiera', err);
            setBillingCutoffDay(null);
            setRecibosConfig(null);
            setFinancialConfig(null);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setChargesState(null);
            return () => { };
        }

        const estadoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'finanzas', 'estado');
        const unsubscribe = onSnapshot(estadoRef, (snapshot) => {
            if (snapshot.exists()) {
                setChargesState(snapshot.data());
            } else {
                setChargesState(null);
            }
        }, (err) => {
            console.error('ResidentStatusPage: error al leer estado de cargos', err);
            setChargesState(null);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    const handleClosePaymentModal = () => {
        setIsPaymentModalOpen(false);
        setPaymentResident(null);
    };

    const handleCloseHistoryModal = () => {
        // Limpiar el listener cuando se cierre
        if (historyUnsubscribe) {
            historyUnsubscribe();
            setHistoryUnsubscribe(null);
        }
        setIsHistoryModalOpen(false);
        setRawIngresos([]);
        setRawHistorial([]);
        setRawExtrasPendientes([]);
        setHistoryResident(null);
        setHistorySearchTerm('');
        setSelectedIngreso(null);

        // Si venimos de la pantalla de búsqueda de residentes, regresar conservando el filtro original (por ejemplo, "2")
        if (location.state?.openHistory) {
            navigate('/administracion/departamentos/residentes', {
                state: {
                    search: location.state?.originalSearch !== undefined ? location.state.originalSearch : ''
                }
            });
        }
    };

    const handleConfirmPago = async (datosPago, options = {}) => {
        if (!paymentResident || !fraccionamientoId) return;

        const pagos = Array.isArray(datosPago) ? datosPago : (datosPago?.pagos || []);
        const totalMonto = datosPago?.totalMonto ?? options?.totalMonto ?? 0;
        const totalDescuento = datosPago?.totalDescuento ?? options?.totalDescuento ?? 0;
        const montoFinal = datosPago?.montoFinal ?? options?.montoFinal ?? 0;
        const folioBase = datosPago?.folioBase ?? options?.folioBase;
        const folioSerie = datosPago?.folioSerie ?? options?.folioSerie;

        console.log('ResidentStatusPage: handleConfirmPago recibió:', { folioBase, folioSerie, pagosLength: pagos?.length });

        // Validar que pagos es un array
        if (!Array.isArray(pagos) || pagos.length === 0) {
            console.error('handleConfirmPago: pagos no válido', datosPago);
            return;
        }

        try {
            const batch = writeBatch(db);
            const ingresoRefs = []; // Guardar referencias para enviar correos después

            // NOTA: Ya NO actualizamos el adeudo aquí manualmente
            // La Cloud Function onIngresoCreated se encargará de actualizar
            // el saldo automáticamente cuando se registre cada ingreso

            // Operación: Crear 1 ÚNICO registro de ingreso unificado para TODAS las partidas
            let downloadURL = null;
            const primerPagoConComprobante = pagos.find(p => p.comprobante);
            if (primerPagoConComprobante) {
                try {
                    const storageRef = ref(storage, `comprobantes/${fraccionamientoId}/${paymentResident.id}_${Date.now()}_${primerPagoConComprobante.comprobante.name}`);
                    const snapshot = await uploadBytes(storageRef, primerPagoConComprobante.comprobante);
                    downloadURL = await getDownloadURL(snapshot.ref);
                } catch (uploadError) {
                    console.error('Error subiendo comprobante en ResidentStatusPage:', uploadError);
                }
            }

            const ingresoRef = doc(collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos'));

            const folioSerieSafe = (folioSerie || 'R').replace(/[^A-Za-z]/g, '') || 'R';
            const folioConsecutivo = (folioBase !== null && folioBase !== undefined && !isNaN(folioBase))
                ? Number(folioBase)
                : null;
            const folioString = folioConsecutivo ? `${folioSerieSafe}${String(folioConsecutivo).padStart(6, '0')}` : null;

            const primerItemPago = pagos[0] || {};
            const conceptosResumen = pagos.map(p => p.concepto).filter(Boolean).join(', ');
            const conceptoFinal = pagos.length > 1 ? `Multi-Concepto: ${conceptosResumen}` : (primerItemPago.concepto || `Pago mantenimiento - Casa ${paymentResident.numero || paymentResident.id}`);

            const obsLista = pagos.map(p => (p.observaciones || '').trim()).filter(Boolean);
            const obsGeneralRoot = (datosPago?.observaciones || options?.observaciones || '').trim();

            const bloquesNotas = [];
            obsLista.forEach(nota => {
                if (!bloquesNotas.includes(nota)) {
                    bloquesNotas.push(nota);
                }
            });

            if (obsGeneralRoot && !bloquesNotas.some(b => b.includes(obsGeneralRoot))) {
                bloquesNotas.push(obsGeneralRoot);
            }

            const observacionesFinales = bloquesNotas.join('\n');

            const totalMontoCalculado = totalMonto || pagos.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
            const totalDescuentoCalculado = totalDescuento || pagos.reduce((sum, p) => sum + (parseFloat(p.descuento) || 0), 0);
            const totalNetoCalculado = montoFinal || pagos.reduce((sum, p) => sum + (parseFloat(p.montoNeto) || 0), 0);

            const cleanPagos = pagos.map(p => {
                const copy = { ...p };
                delete copy.comprobante; // Eliminar objeto File no serializable
                return copy;
            });

            // A. Calcular montos aplicados a cada concepto y deudas restantes pos-pago
            let aplicadoACapital = 0;
            let aplicadoAIntereses = 0;
            let aplicadoAAgua = 0;
            let aplicadoAExtras = 0;
            let aplicadoAMultas = 0;
            let saldoFavorConsumidoPago = 0;
            let saldoFavorGeneradoPago = Number(datosPago?.saldoFavorGenerado || options?.saldoFavorGenerado || 0);

            pagos.forEach(p => {
                const concLower = String(p.concepto || '').toLowerCase();
                const montoPartida = Number(p.montoNeto || p.monto || 0);

                if (!esPartidaContableResidente(p)) {
                    // CATEGORÍA B: Misceláneo / Tesorería (no amortiza adeudo ni genera saldo a favor)
                    if (p.saldoFavorConsumido > 0) {
                        saldoFavorConsumidoPago += Number(p.saldoFavorConsumido);
                    }
                    return;
                }

                if (concLower.includes('agua') || concLower.includes('cea') || p.periodoAguaId) {
                    aplicadoAAgua += montoPartida;
                } else if (concLower.includes('extraordinaria') || concLower.includes('cuota extra') || p.cuotaExtraId) {
                    aplicadoAExtras += montoPartida;
                } else if (concLower.includes('multa') || p.multaAdminId) {
                    aplicadoAMultas += montoPartida;
                } else if (concLower.includes('recargo') || concLower.includes('interes')) {
                    aplicadoAIntereses += montoPartida;
                } else {
                    // Es cuota de mantenimiento ordinaria (Categoría A)
                    const apCap = Number(p.aplicadoACapital || 0);
                    const apInt = Number(p.aplicadoAIntereses || 0);
                    if (apCap > 0 || apInt > 0) {
                        aplicadoACapital += apCap;
                        aplicadoAIntereses += apInt;
                    } else {
                        // Fallback: todo a capital
                        aplicadoACapital += montoPartida;
                    }
                }
                
                if (p.saldoFavorConsumido > 0) {
                    saldoFavorConsumidoPago += Number(p.saldoFavorConsumido);
                }
            });

            const capAnterior = Number(paymentResident.adeudoCapital || 0);
            const intAnterior = Number(paymentResident.adeudoIntereses || 0);
            const aguaAnterior = Number(paymentResident.adeudoAgua || 0);
            const extrasAnterior = Number(paymentResident.cuotasExtras || 0);
            const multasAnterior = Number(paymentResident.adeudoMultas || 0);
            const sfAnterior = Number(paymentResident.saldoFavor || 0);

            const adeudoCapitalNuevo = Math.max(0, Math.round((capAnterior - aplicadoACapital) * 100) / 100);
            const adeudoInteresesNuevo = Math.max(0, Math.round((intAnterior - aplicadoAIntereses) * 100) / 100);
            const adeudoAguaNuevo = Math.max(0, Math.round((aguaAnterior - aplicadoAAgua) * 100) / 100);
            const cuotasExtrasNuevo = Math.max(0, Math.round((extrasAnterior - aplicadoAExtras) * 100) / 100);
            const adeudoMultasNuevo = Math.max(0, Math.round((multasAnterior - aplicadoAMultas) * 100) / 100);
            
            const saldoFavorNuevo = Math.max(0, Math.round((sfAnterior - saldoFavorConsumidoPago + saldoFavorGeneradoPago) * 100) / 100);
            const adeudoNuevo = Math.max(0, Math.round((adeudoCapitalNuevo + adeudoInteresesNuevo + adeudoAguaNuevo + cuotasExtrasNuevo + adeudoMultasNuevo - saldoFavorNuevo) * 100) / 100);

            const singleIngresoData = {
                fraccionamientoId,
                monto: parseFloat(totalMontoCalculado || 0),
                descuento: parseFloat(totalDescuentoCalculado || 0),
                descuentoPorcentaje: 0,
                montoNeto: parseFloat(totalNetoCalculado || 0),
                mesesPagadosAdelantado: pagos.reduce((sum, p) => sum + (p.mesesPagadosAdelantado || 0), 0),
                concepto: conceptoFinal,
                casaId: paymentResident.id,
                casaNumero: paymentResident.numero || '',
                fecha: serverTimestamp(),
                timestamp: serverTimestamp(),
                fechaPagoOriginal: primerItemPago.fechaPago || null,
                metodoPago: primerItemPago.metodoPago || '',
                referencia: primerItemPago.referencia || '',
                observaciones: observacionesFinales,
                comprobanteURL: downloadURL || null,
                pagos: cleanPagos, // <--- Arreglo unificado sin objetos File
                esMultiConcepto: pagos.length > 1,
                
                // Campos de desglose contable e históricos posterior al pago
                aplicadoACapital,
                aplicadoAIntereses,
                aplicadoAAgua,
                aplicadoAExtras,
                aplicadoAMultas,
                saldoFavorGenerado: saldoFavorGeneradoPago,
                saldoFavorConsumido: saldoFavorConsumidoPago,
                
                adeudoCapitalAnterior: capAnterior,
                adeudoInteresesAnterior: intAnterior,
                adeudoAguaAnterior: aguaAnterior,
                cuotasExtrasAnterior: extrasAnterior,
                adeudoMultasAnterior: multasAnterior,
                saldoFavorAnterior: sfAnterior,

                adeudoCapitalNuevo,
                adeudoInteresesNuevo,
                adeudoAguaNuevo,
                cuotasExtrasNuevo,
                adeudoMultasNuevo,
                saldoFavorNuevo,
                adeudoNuevo,

                ...(folioConsecutivo ? { folioConsecutivo, folioSerie: folioSerieSafe, folio: folioString } : {}),
            };

            const sanitizeObject = (obj) => JSON.parse(JSON.stringify(obj, (k, v) => v === undefined ? null : v));
            const firestoreIngresoPayload = {
                ...sanitizeObject(singleIngresoData),
                fecha: serverTimestamp(),
                timestamp: serverTimestamp()
            };

            ingresoRefs.push({ ref: ingresoRef, pago: firestoreIngresoPayload, downloadURL });
            batch.set(ingresoRef, firestoreIngresoPayload);

            await batch.commit();

            // Procesar actualizaciones asíncronas de agua potable si aplica
            for (const pago of pagos) {
                const esAgua = String(pago.concepto || '').toLowerCase().includes('agua') || String(pago.concepto || '').toLowerCase().includes('cea');
                if (esAgua && pago.periodoAguaId && paymentResident?.id) {
                    const cId = paymentResident.id;

                    // Si se consumió saldo a favor, restarlo de la casa de forma persistente
                    if (pago.saldoFavorConsumido > 0) {
                        try {
                            const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', cId);
                            const casaDocSnap = await getDoc(casaDocRef);
                            if (casaDocSnap.exists()) {
                                const currentSF = Number(casaDocSnap.data().saldoFavor || 0);
                                const newSF = Math.max(0, Math.round((currentSF - Number(pago.saldoFavorConsumido)) * 100) / 100);
                                await updateDoc(casaDocRef, { saldoFavor: newSF });
                                console.log(`✓ Se descontaron $${pago.saldoFavorConsumido} de saldo a favor de la casa en ResidentStatusPage. Nuevo SF: $${newSF}`);
                            }
                        } catch (errSF) {
                            console.error('Error al descontar saldo a favor de la casa en ResidentStatusPage:', errSF);
                        }
                    }

                    try {
                        const consumoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable', pago.periodoAguaId);
                        const consumoSnap = await getDoc(consumoRef);
                        if (consumoSnap.exists()) {
                            const dataConsumo = consumoSnap.data();
                            const desglose = dataConsumo.desgloseCasas || [];
                            const indexCasa = desglose.findIndex(c => String(c.casaId || '').trim().toUpperCase() === String(cId).trim().toUpperCase());
                            
                            // 1. Marcar el desglose como PAGADO en consumosAguaPotable
                            if (indexCasa >= 0) {
                                desglose[indexCasa].pagado = true;
                                desglose[indexCasa].estadoPago = 'PAGADO';
                                desglose[indexCasa].montoPendiente = 0;
                                await updateDoc(consumoRef, { desgloseCasas: desglose });
                                console.log('✓ Se actualizó el período de agua a PAGADO en consumosAguaPotable en ResidentStatusPage');

                                // 1.1 Descontar el cargo de agua del adeudo total de la casa en Firestore
                                const cargoAgua = Number(desglose[indexCasa]?.cargoTotal || desglose[indexCasa]?.monto || desglose[indexCasa]?.montoPendiente || 0);
                                if (cargoAgua > 0) {
                                    try {
                                        const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', cId);
                                        const casaSnap = await getDoc(casaDocRef);
                                        if (casaSnap.exists()) {
                                            const currentAdeudo = Number(casaSnap.data().adeudo || 0);
                                            const currentCapital = Number(casaSnap.data().adeudoCapital || 0);
                                            const newAdeudo = Math.max(0, Math.round((currentAdeudo - cargoAgua) * 100) / 100);
                                            const newCapital = Math.max(0, Math.round((currentCapital - cargoAgua) * 100) / 100);
                                            const idNorm = String(cId).trim().toUpperCase();
                                            const numNorm = String(casaSnap.data().numero || casaSnap.data().numeroCasa || '').trim().toUpperCase();
                                            const aguaPendienteActual = (mapAguaPendiente[idNorm] || 0) || (mapAguaPendiente[numNorm] || 0);
                                            const tieneMasAgua = aguaPendienteActual > cargoAgua;

                                            await updateDoc(casaDocRef, {
                                                adeudo: newAdeudo,
                                                adeudoCapital: newCapital,
                                                tieneAdeudoAguaVariable: tieneMasAgua,
                                                adeudoAgua: tieneMasAgua ? Math.max(0, aguaPendienteActual - cargoAgua) : 0,
                                                montoAguaPendiente: tieneMasAgua ? Math.max(0, aguaPendienteActual - cargoAgua) : 0
                                            });
                                            console.log(`✓ [ResidentStatusPage] Se descontaron $${cargoAgua} del adeudo de la casa. Nuevo adeudo: $${newAdeudo}, tieneAdeudoAguaVariable: ${tieneMasAgua}`);
                                        }
                                    } catch (errCasaUpdate) {
                                        console.error('Error al actualizar adeudo de la casa por agua en ResidentStatusPage:', errCasaUpdate);
                                    }
                                }
                            }

                            // 2. Marcar el documento de mesesPendientes de la casa como PAGADO
                            const mesLectura = dataConsumo.mesLectura || 'ACTUAL';
                            const anoLectura = dataConsumo.anoLectura || new Date().getFullYear();
                            const pendienteId = `AGUA-${cId}-${mesLectura}-${anoLectura}`;
                            const pendienteRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', cId, 'mesesPendientes', pendienteId);
                            
                            try {
                                const pendSnap = await getDoc(pendienteRef);
                                if (pendSnap.exists()) {
                                    const cargoAgua = Number(desglose[indexCasa]?.cargoTotal || desglose[indexCasa]?.monto || desglose[indexCasa]?.montoPendiente || 284.00);
                                    await updateDoc(pendienteRef, {
                                        estado: 'pagado',
                                        estadoResidente: 'AL CORRIENTE',
                                        cuotaMensual: cargoAgua > 0 ? cargoAgua : 284.00,
                                        montoPagado: cargoAgua > 0 ? cargoAgua : 284.00,
                                        montoPendiente: 0,
                                        colorBandera: 'verde',
                                        pagos: [{
                                            ingresoId: ingresoRef.id,
                                            monto: cargoAgua > 0 ? cargoAgua : 284.00,
                                            fecha: new Date().toISOString()
                                        }]
                                    });
                                    console.log('✓ Se actualizó mesesPendientes a pagado con montoPagado para agua en ResidentStatusPage:', pendienteId);
                                }
                            } catch (errPend) {
                                console.warn('No se pudo encontrar o actualizar mesesPendientes para agua en ResidentStatusPage:', errPend);
                            }
                        }
                    } catch (errConsumo) {
                        console.error('Error al procesar el pago de agua variable en ResidentStatusPage:', errConsumo);
                    }
                }
            }

            // Procesar actualizaciones directas de sanciones administrativas pagadas
            for (const pago of pagos) {
                const cLower = String(pago.concepto || '').toLowerCase();
                const esSancion = cLower.includes('sancion') || cLower.includes('sansion') || cLower.includes('multa');
                if (esSancion && paymentResident?.id) {
                    try {
                        const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
                        let multaDocRef = null;
                        let multaTargetData = null;

                        // 1. Si tenemos el docId directo de la sanción
                        if (pago.multaAdminDocId) {
                            const directSnap = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas', pago.multaAdminDocId));
                            if (directSnap.exists() && !directSnap.data().pagada) {
                                multaDocRef = directSnap.ref;
                                multaTargetData = directSnap.data();
                            }
                        }

                        // 2. Si no, buscar por casaId o casaNumero
                        if (!multaDocRef) {
                            let qMultas = query(multasRef, where('casaId', '==', paymentResident.id));
                            let snapMultas = await getDocs(qMultas);
                            if (snapMultas.empty && paymentResident.numero) {
                                qMultas = query(multasRef, where('casaNumero', '==', String(paymentResident.numero).trim()));
                                snapMultas = await getDocs(qMultas);
                            }
                            const multasActivas = snapMultas.docs.filter(d => !d.data().pagada);
                            const multaTarget = multasActivas.find(d => {
                                const data = d.data();
                                if (pago.multaAdminId && (String(d.id) === String(pago.multaAdminId) || String(data.multaId) === String(pago.multaAdminId))) return true;
                                if (pago.multaAdminDesc && data.multaDescripcion === pago.multaAdminDesc) return true;
                                if (pago.multaAdministrativa && data.multaDescripcion === pago.multaAdministrativa) return true;
                                return false;
                            }) || multasActivas[0];

                            if (multaTarget) {
                                multaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas', multaTarget.id);
                                multaTargetData = multaTarget.data();
                            }
                        }

                        if (multaDocRef) {
                            await updateDoc(multaDocRef, {
                                pagada: true,
                                estado: 'saldada',
                                fechaPago: serverTimestamp(),
                                ingresoId: ingresoRef.id,
                                folioPago: folioString || null
                            });
                            console.log('✓ [ResidentStatusPage] Se actualizó sanción administrativa a SALDADA en Firestore:', multaDocRef.id);
                        }

                        // 3. Descontar el cargo de la sanción del adeudo total de la casa en Firestore
                        const cargoSancion = Number(pago.montoNeto || pago.monto || multaTargetData?.monto || 0);
                        if (cargoSancion > 0) {
                            try {
                                const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', paymentResident.id);
                                const casaSnap = await getDoc(casaDocRef);
                                if (casaSnap.exists()) {
                                    const cData = casaSnap.data() || {};
                                    const currentAdeudo = Number(cData.adeudo || 0);
                                    const currentCapital = Number(cData.adeudoCapital || 0);
                                    const currentMultas = Number(cData.adeudoMultas || cData.sancionesAdmin || 0);
                                    const newAdeudo = Math.max(0, Math.round((currentAdeudo - cargoSancion) * 100) / 100);
                                    const newCapital = Math.max(0, Math.round((currentCapital - cargoSancion) * 100) / 100);
                                    const newMultas = Math.max(0, Math.round((currentMultas - cargoSancion) * 100) / 100);

                                    await updateDoc(casaDocRef, {
                                        adeudo: newAdeudo,
                                        adeudoCapital: newCapital,
                                        adeudoMultas: newMultas,
                                        sancionesAdmin: newMultas
                                    });
                                    console.log(`✓ [ResidentStatusPage] Se descontaron $${cargoSancion} del adeudo de la casa por sanción. Nuevo adeudo: $${newAdeudo}`);
                                }
                            } catch (errCasaUpdate) {
                                console.error('Error al actualizar adeudo de la casa por sanción administrativa en ResidentStatusPage:', errCasaUpdate);
                            }
                        }
                    } catch (errMulta) {
                        console.warn('Error al marcar sanción como pagada en ResidentStatusPage:', errMulta);
                    }
                }
            }

            // Enviar recibos por correo automáticamente si hay email registrado
            const emailDestino = pickFirstString([
                paymentResident?.email,
                paymentResident?.correo,
                paymentResident?.correoElectronico,
                paymentResident?.correo_residente,
            ]);

            if (emailDestino) {
                // Cargar configuración de recibos
                let configToSend = {};
                try {
                    const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                    if (fraccDoc.exists()) {
                        const data = fraccDoc.data() || {};
                        configToSend = data.configuracionRecibos || {};
                        // fallback a legacy root-level fields
                        const rootConfig = {};
                        const allowed = [
                            'logoUrl', 'logoWidth', 'logoHeight',
                            'selloUrl', 'selloWidth', 'selloHeight',
                            'razonSocial', 'rfc', 'datosFacturacion',
                            'adminEmail', 'administradorNombre', 'adminDireccion',
                            'condominioNombre', 'condominioDireccion',
                            'folioSerie', 'folioConsecutivo', 'folioSerieEgreso', 'folioConsecutivoEgreso',
                            'moneda'
                        ];
                        allowed.forEach((k) => {
                            if (data[k] !== undefined) rootConfig[k] = data[k];
                        });
                        configToSend = { ...rootConfig, ...configToSend };
                    }
                } catch (configErr) {
                    console.error('ResidentStatusPage: error al cargar configuracionRecibos', configErr);
                    configToSend = recibosConfig || {};
                }

                const sendReceiptEmail = httpsCallable(functions, 'sendReceiptEmail');

                for (const { ref: ingresoRef } of ingresoRefs) {
                    try {
                        let ingresoCompleto = null;
                        if (!navigator.onLine) {
                            const localSnap = await getDoc(ingresoRef);
                            ingresoCompleto = { id: localSnap.id, ...(localSnap.data() || {}) };
                        } else {
                            // Esperar a que el ingreso exista y tenga datos completos (procesado por Cloud Function)
                            for (let i = 0; i < 15; i++) {
                                await new Promise(resolve => setTimeout(resolve, 800)); // ~12 segundos total
                                try {
                                    const snap = await getDocFromServer(ingresoRef);
                                    if (snap.exists()) {
                                        const data = snap.data() || {};
                                        // onIngresoCreated escribe mesesAplicados o adeudoNuevo; esperar hasta que existan
                                        const procesado = data.mesesAplicados !== undefined || data.adeudoNuevo !== undefined || data.aplicadoAIntereses !== undefined;
                                        if (procesado || i >= 14) {
                                            ingresoCompleto = { id: snap.id, ...data };
                                            break;
                                        }
                                    }
                                } catch (e) {
                                    console.warn('ResidentStatusPage: error en getDocFromServer, reintentando...', e);
                                }
                            }
                        }

                        if (ingresoCompleto) {
                            const folioIndex = await computeFolioIndex(fraccionamientoId, ingresoRef.id);
                            const ingresoConFolio = { ...ingresoCompleto, folioIndex };

                            // Preparar datos completos de la casa incluyendo teléfono
                            const telefono = pickFirstString([
                                paymentResident?.telefono,
                                paymentResident?.telefono_residente,
                                paymentResident?.tel,
                                paymentResident?.celular,
                                paymentResident?.telefonoPropietario,
                            ]);

                            const casaInfo = {
                                ...paymentResident,
                                fraccionamientoId,
                                numero: paymentResident.numero || paymentResident.numeroCasa || paymentResident.lote || '',
                                propietario: paymentResident.propietario || paymentResident.nombre || paymentResident.nombre_residente || 'Residente',
                                nombre_residente: paymentResident.nombre_residente || paymentResident.nombre || paymentResident.propietario || 'Residente',
                                telefono,
                                email: emailDestino,
                            };

                            const payloadToSend = {
                                email: emailDestino,
                                fraccId: fraccionamientoId,
                                receipt: ingresoConFolio,
                                config: configToSend,
                                casaInfo: { ...casaInfo, fraccionamientoId },
                            };

                            if (!navigator.onLine) {
                                pushToSyncQueue('sendReceiptEmail', payloadToSend);
                                console.log('ResidentStatusPage: Recibo encolado para envío offline.');
                                await updateDoc(ingresoRef, {
                                    emailEnviado: false,
                                    emailEnviadoOffline: true,
                                    emailEnviadoA: emailDestino,
                                });
                            } else {
                                const emailResp = await sendReceiptEmail(payloadToSend);
                                if (emailResp?.data?.success) {
                                    await updateDoc(ingresoRef, {
                                        emailEnviado: true,
                                        emailEnviadoEn: serverTimestamp(),
                                        emailEnviadoA: emailDestino,
                                    });
                                }
                            }
                        }
                    } catch (emailErr) {
                        console.error('ResidentStatusPage: error enviando recibo individual', emailErr);
                    }
                }
            }

            try {
                // Registrar movimiento agrupado
                const deudaActual = Number(paymentResident.adeudo || 0);
                const nuevaDeuda = Math.max(0, deudaActual - totalMonto);

                await registerMovement({
                    fraccionamientoId,
                    titulo: `Pago Recibido - Casa ${paymentResident.numero || paymentResident.id}`,
                    descripcion: `Se registraron ${pagos.length} pago(s) por un total de ${currencyFormatter.format(totalMonto)}. Deuda estimada restante: ${currencyFormatter.format(nuevaDeuda)}.`,
                    categoria: 'Pagos',
                    tipoMovimiento: 'registro_pago',
                    origen: 'resident_status',
                    metadata: {
                        casaId: paymentResident.id,
                        casaNumero: paymentResident.numero || '',
                        cantidadPagos: pagos.length,
                        montoTotal: Number(totalMonto),
                        deudaRestante: Number(nuevaDeuda),
                    },
                    userData,
                    currentUser,
                });
            } catch (logErr) {
                console.error('ResidentStatusPage: error al registrar movimiento del pago', logErr);
            }

            // Actualizar UI localmente
            // Nota: usamos fecha del primer pago como referencia visual si hay múltiples
            const primerPago = pagos[0];
            const fechaVisual = primerPago ? new Date(primerPago.fechaPago) : new Date();

            setResidents((prev) => prev.map((r) => (
                r.id === paymentResident.id
                    ? { ...r, fechaUltimoPago: fechaVisual }
                    : r
            )));

            // Mostrar mensaje de éxito
            const deudaActualModal = Number(paymentResident.adeudo || 0);
            const nuevaDeudaModal = Math.max(0, deudaActualModal - totalMonto);
            const esMoroso = nuevaDeudaModal > 0;
            const estadoDeuda = esMoroso ? `Deuda estimada: ${currencyFormatter.format(nuevaDeudaModal)}` : 'Deuda pagada completamente';

            setConfirmationModal({
                open: true,
                title: 'Pago Registrado',
                message: `Se ha registrado exitosamente ${pagos.length} pago(s) por ${currencyFormatter.format(totalMonto)} para la casa ${paymentResident.numero || paymentResident.id}. ${estadoDeuda}. El saldo se actualizará automáticamente.`,
                type: 'success',
            });

        } catch (err) {
            console.error('ResidentStatusPage: error al registrar pagos', err);
            setConfirmationModal({
                open: true,
                title: 'Error al Registrar Pago',
                message: `Hubo un error al procesar el pago: ${err.message || 'Error de conexión'}. Intente nuevamente.`,
                type: 'error',
            });
        } finally {
            // El listener en tiempo real actualizará automáticamente las casas.
            // Se quitó handleClosePaymentModal() de aquí por requerimiento para no cerrar el modal de cobro mientras se ve la ventana emergente de confirmación.
        }
    };

    const pickFirstString = useCallback((values) => {
        for (const value of values) {
            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed.length > 0) {
                    return trimmed;
                }
            }
        }
        return '';
    }, []);

    const computeFolioIndex = useCallback(async (fraccId, ingresoId) => {
        try {
            const q = query(collection(db, 'fraccionamientos', fraccId, 'ingresos'), orderBy('fecha', 'desc'));
            const snap = await getDocs(q);
            const ids = snap.docs.map(d => d.id);
            const pos = ids.indexOf(ingresoId);
            if (pos === -1) return 0;
            return snap.size - 1 - pos; // mismo cálculo que en ListaIngresos
        } catch (err) {
            console.error('ResidentStatusPage: computeFolioIndex error', err);
            return 0;
        }
    }, []);

    const currencyFormatter = useMemo(() => (
        new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
    ), []);

    const formatDateTimeLabel = useCallback((value) => {
        if (!value) return '—';
        const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }, []);

    const isResidentMoroso = useCallback((resident) => {
        const saldoFavor = Number(resident?.saldoFavor ?? 0);
        // Si el residente tiene saldo a favor, nunca es moroso
        if (saldoFavor > 0) {
            return false;
        }
        const adeudo = Number(resident?.adeudo ?? resident?.deuda ?? resident?.montoAdeudo ?? 0);
        if (!Number.isFinite(adeudo)) {
            return false;
        }
        // Nueva regla: moroso solo si adeudo >= (cuotaMensual × mesesPermitidos)
        const cuotaMensual = Number(financialConfig?.cuotaMensual || financialConfig?.montoMensual || 0);
        const mesesPermitidos = Number(financialConfig?.interesesMesesPermitidosDeuda ?? financialConfig?.mesesPermitidos ?? 1);
        return calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
    }, [financialConfig]);

    const calcularAdeudoVisible = useCallback((resident) => {
        // Usar la utilidad compartida que respeta el período de gracia
        return calcularAdeudoVisibleDesdeObjeto(resident, financialConfig);
    }, [financialConfig]);

    const isCasetaProfile = useCallback((resident) => {
        return esCasaCaseta(resident);
    }, []);

    const residentSummary = useMemo(() => {
        const visibles = residents.filter((res) => !isCasetaProfile(res));
        const total = visibles.length;
        const morososCount = visibles.filter(isResidentMoroso).length;
        const corrientesCount = Math.max(total - morososCount, 0);
        return { total, morososCount, corrientesCount };
    }, [residents, isResidentMoroso, isCasetaProfile]);

    const statusResidents = useMemo(() => {
        const filteredByStatus = residents
            .filter((resident) => !isCasetaProfile(resident))
            .filter((resident) => (
                isMorosoView ? isResidentMoroso(resident) : !isResidentMoroso(resident)
            ));

        return [...filteredByStatus].sort(compararPorNumeroCasa);
    }, [residents, isMorosoView, isResidentMoroso, isCasetaProfile]);

    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredResidents = useMemo(() => {
        if (!normalizedSearch) {
            return statusResidents;
        }

        // Búsqueda global: Ignoramos el estado de morosidad si hay un término de búsqueda
        const baseResidents = residents.filter((resident) => !isCasetaProfile(resident));

        const filtered = baseResidents.filter((resident) => {
            const searchFields = [
                resident.numero,
                resident.numeroCasa,
                resident.lote,
                resident.numero ? `casa ${resident.numero}` : '',
                resident.numeroCasa ? `casa ${resident.numeroCasa}` : '',
                resident.nombre_residente,
                resident.nombre,
                resident.nombreTitular,
                resident.titular,
                resident.propietario,
                resident.arrendador,
                resident.nombrePropietario,
                resident.nombreCompleto,
                resident.email,
                resident.correo,
            ];

            return searchFields.some((field) => {
                if (field === undefined || field === null) {
                    return false;
                }
                return String(field).toLowerCase().includes(normalizedSearch);
            });
        });

        return [...filtered].sort(compararPorNumeroCasa);
    }, [statusResidents, residents, normalizedSearch, isCasetaProfile]);

    const totalAdeudo = useMemo(() => {
        if (!isMorosoView) {
            return 0;
        }
        return statusResidents.reduce((sum, resident) => {
            const amount = Number(resident.adeudo ?? resident.deuda ?? resident.montoAdeudo ?? 0);
            if (!Number.isFinite(amount)) {
                return sum;
            }
            return sum + amount;
        }, 0);
    }, [statusResidents, isMorosoView]);

    const renderInfoRow = (row) => {
        const isClickable = typeof row.onClick === 'function';

        // Tratamiento especializado para adeudo de agua
        if (row.key === 'deuda-agua' && Array.isArray(row.periodosAgua)) {
            const periodos = row.periodosAgua;
            const tieneMultiples = periodos.length > 1;
            const isWaterExpanded = tieneMultiples && !!expandedWaterCards[row.casaKey];

            return (
                <div
                    key={row.key}
                    className={[
                        'resident-info-row',
                        row.variant ? row.variant : '',
                        'resident-info-row-water',
                        isWaterExpanded ? 'water-is-expanded' : '',
                    ].filter(Boolean).join(' ')}
                >
                    <div className="resident-info-water-card-content">
                        <div className="resident-info-icon">{row.icon}</div>
                        <div className="resident-info-text resident-info-water-text">
                            <div className="water-concept-header">
                                <span className="resident-info-label">{row.title}</span>
                                {tieneMultiples ? (
                                    <button
                                        type="button"
                                        className="water-dropdown-pill-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedWaterCards(prev => ({
                                                ...prev,
                                                [row.casaKey]: !prev[row.casaKey]
                                            }));
                                        }}
                                        title={isWaterExpanded ? 'Contraer detalle por mes' : 'Desplegar detalle por mes'}
                                    >
                                        <span>{periodos.length} períodos</span>
                                        {isWaterExpanded ? <FaChevronUp className="water-chevron-icon" /> : <FaChevronDown className="water-chevron-icon" />}
                                    </button>
                                ) : (
                                    periodos.length === 1 && (
                                        <span className="water-period-middle-badge" title={`Período: ${periodos[0].periodo}`}>
                                            {periodos[0].periodo}
                                        </span>
                                    )
                                )}
                            </div>
                            <span className="resident-info-value">{row.value}</span>
                        </div>
                    </div>

                    {isWaterExpanded && (
                        <div className="water-breakdown-panel">
                            <div className="water-breakdown-header">
                                <span>Desglose por Mes:</span>
                            </div>
                            <div className="water-breakdown-items-list">
                                {periodos.map((p, pIdx) => (
                                    <div key={p.id || p.key || pIdx} className="water-breakdown-row">
                                        <span className="water-breakdown-month">
                                            <FaCalendarAlt className="water-breakdown-cal-icon" />
                                            {p.periodo}
                                        </span>
                                        <span className="water-breakdown-amount">
                                            {currencyFormatter.format(p.monto)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="water-breakdown-footer">
                                <span>Total Adeudo Agua:</span>
                                <strong>{row.value}</strong>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                key={row.key}
                className={[
                    'resident-info-row',
                    row.variant ? row.variant : '',
                    row.muted ? 'muted' : '',
                    isClickable ? 'clickable-row' : '',
                ].filter(Boolean).join(' ')}
                onClick={isClickable ? row.onClick : undefined}
                style={isClickable ? { cursor: 'pointer', userSelect: 'none' } : {}}
            >
                <div className="resident-info-icon">{row.icon}</div>
                <div className="resident-info-text">
                    <div className="resident-info-label-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                        <span className="resident-info-label">{row.title}</span>
                        {row.hasBreakdown && (
                            <button
                                type="button"
                                className={`row-desglose-chip-btn ${row.breakdownVariant || ''}`}
                                onClick={row.onBreakdown}
                                title={`Ver desglose analítico de ${row.title}`}
                            >
                                <span>{row.breakdownLabel || 'Ver desglose'}</span>
                                <FaChevronRight style={{ fontSize: '0.62rem' }} />
                            </button>
                        )}
                    </div>
                    <span className="resident-info-value" style={isClickable ? { color: '#6b21a8', fontWeight: 'bold' } : {}}>{row.value}</span>
                </div>
            </div>
        );
    };

    const StatusIcon = isMorosoView ? FaUserTimes : FaUserCheck;

    const handleSearchSubmit = (event) => {
        event.preventDefault();
    };

    const handleDownloadReceipt = async (ingreso) => {
        try {
            setSendingStatus('loading');

            // Obtener configuración de recibos si está disponible
            const cfg = recibosConfig || {};

            // Funciones auxiliares para formato
            const moneda = 'MXN';
            const formatMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(n || 0);
            const formatDate = (date) => {
                if (!date) return '---';
                const d = date.toDate ? date.toDate() : new Date(date);
                return d.toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
            };

            // Convertir número a letras (simplificado)
            const numberToWords = (num) => {
                const ones = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
                const tens = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
                const twenties = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
                const scales = ['', 'mil', 'millón'];

                if (num === 0) return 'cero';

                const parts = [];
                let scaleIndex = 0;

                while (num > 0 && scaleIndex < scales.length) {
                    const part = num % 1000;
                    if (part !== 0) {
                        let partWords = '';
                        const hundreds = Math.floor(part / 100);
                        const remainder = part % 100;

                        if (hundreds === 1) partWords += 'cien';
                        else if (hundreds > 1) partWords += ['', '', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'][hundreds] + 'cientos';

                        if (remainder > 0) {
                            if (partWords) partWords += ' ';
                            if (remainder < 10) partWords += ones[remainder];
                            else if (remainder < 20) partWords += tens[remainder - 10];
                            else {
                                const tens_digit = Math.floor(remainder / 10);
                                const ones_digit = remainder % 10;
                                partWords += twenties[tens_digit];
                                if (ones_digit > 0) partWords += ' y ' + ones[ones_digit];
                            }
                        }

                        if (scaleIndex > 0) partWords += ' ' + scales[scaleIndex];
                        if (parts.length > 0) partWords += ' ';
                        parts.unshift(partWords);
                    }
                    num = Math.floor(num / 1000);
                    scaleIndex++;
                }

                return parts.join('').trim();
            };

            const total = (ingreso.monto || 0) - (ingreso.descuento || 0);
            const totalLetras = numberToWords(Math.floor(total)) + ' pesos';
            // Calcular folio usando serie y consecutivo sobre la lista ordenada (más reciente arriba)
            const serie = (cfg.folioSerie || 'R').replace(/[^A-Za-z]/g, '') || 'R';
            const folioConsecutivo = cfg.folioConsecutivo || 1;
            const sortedHistory = historyIngresos
                .slice()
                .sort((a, b) => {
                    const da = (a.timestamp?.toDate ? a.timestamp.toDate() : (a.timestamp ? new Date(a.timestamp) : null))
                        || (a.fecha?.toDate ? a.fecha.toDate() : (a.fecha ? new Date(a.fecha) : null))
                        || new Date(0);
                    const db = (b.timestamp?.toDate ? b.timestamp.toDate() : (b.timestamp ? new Date(b.timestamp) : null))
                        || (b.fecha?.toDate ? b.fecha.toDate() : (b.fecha ? new Date(b.fecha) : null))
                        || new Date(0);
                    return db - da; // más reciente primero
                });
            const pos = sortedHistory.findIndex(ing => ing.id === ingreso.id);
            const folioIndex = pos >= 0 ? (sortedHistory.length - 1 - pos) : 0;
            const folio = `${serie}${String(folioConsecutivo + folioIndex).padStart(6, '0')}`;
            const formatFechaPago = (dateStr) => {
                if (!dateStr || typeof dateStr !== 'string') return '';
                const parts = dateStr.split('T')[0].split('-');
                if (parts.length !== 3) return '';
                const [year, month, day] = parts;
                return `${day}/${month}/${year}`;
            };
            const fechaPagoStr = ingreso.fechaPagoOriginal ? formatFechaPago(ingreso.fechaPagoOriginal) : ingreso.fecha ? new Date(ingreso.fecha.toDate ? ingreso.fecha.toDate() : ingreso.fecha).toLocaleDateString('es-MX') : new Date().toLocaleDateString('es-MX');
            const fechaEmision = ingreso.fecha ? new Date(ingreso.fecha.toDate ? ingreso.fecha.toDate() : ingreso.fecha) : new Date();

            const receiptHtml = `
                <div class="receipt-preview">
                    <div class="preview-row preview-top">
                        <div>
                            ${cfg.logoUrl ? `<img src="${cfg.logoUrl}" alt="Logo" class="preview-logo" />` : '<div class="preview-logo placeholder">Logo</div>'}
                            <div class="preview-condo">
                                <h4>${cfg.condominioNombre || userData?.fraccionamiento || 'Fraccionamiento'}</h4>
                                <p>${cfg.condominioDireccion || ''}</p>
                            </div>
                        </div>
                        <div class="preview-right">
                            <h3 class="preview-title">RECIBO DE PAGO</h3>
                            <div class="preview-meta">
                                <span>Folio: ${folio}</span>
                                <span>Fecha de pago: ${fechaPagoStr}</span>
                                <span>Fecha de emisión: ${formatDate(fechaEmision)} ${fechaEmision.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                        </div>
                    </div>

                    <hr class="preview-divider" />

                    <div class="preview-block">
                        <h5>Datos de Facturación</h5>
                        <p><strong>Razón Social:</strong> ${cfg.razonSocial || '---'}</p>
                        <p><strong>RFC:</strong> ${cfg.rfc || '---'}</p>
                        ${cfg.datosFacturacion ? `<p class="preview-muted">${cfg.datosFacturacion}</p>` : ''}
                    </div>

                    <hr class="preview-divider" />

                    <div class="preview-block">
                        <h5>Administrador</h5>
                        <p><strong>Nombre:</strong> ${cfg.administradorNombre || '---'}</p>
                        <p><strong>Dirección:</strong> ${cfg.adminDireccion || '---'}</p>
                    </div>

                    <hr class="preview-divider" />

                    <div class="preview-block">
                        <h5>Residente</h5>
                        <p><strong>Casa:</strong> ${historyResident.numero || '---'}</p>
                        <p><strong>Nombre:</strong> ${historyResident.propietario || historyResident.nombre_residente || historyResident.nombre || '---'}</p>
                        <p><strong>Email:</strong> ${historyResident.email || historyResident.correo || '---'}</p>
                        <p><strong>Teléfono:</strong> ${historyResident.telefono || '---'}</p>
                    </div>

                    <hr class="preview-divider" />

                    <div class="preview-block">
                        <h5>Conceptos de Pago</h5>
                        <div class="preview-table">
                            <div class="preview-table-header">
                                <span>Concepto</span>
                                <span>Importe</span>
                            </div>
                            ${(() => {
                                const conceptoLowerRec = String(ingreso.concepto || '').toLowerCase();
                                const esPagoAnualRec = conceptoLowerRec.includes('anual') || ingreso.esPagoAnual;
                                const partidasArrayRec = (Array.isArray(ingreso.pagos) && ingreso.pagos.length > 0)
                                    ? ingreso.pagos
                                    : (Array.isArray(ingreso.desgloseConceptos) && ingreso.desgloseConceptos.length > 0)
                                        ? ingreso.desgloseConceptos
                                        : null;
                                if (partidasArrayRec) {
                                    return partidasArrayRec.map(item => {
                                        const detStr = item.detalle ? (' &middot; ' + item.detalle) : '';
                                        return `<div class="preview-table-row">
                                            <span>${item.concepto}${detStr}</span>
                                            <span>${formatMoney(item.montoNeto || item.monto)}</span>
                                        </div>`;
                                    }).join('');
                                }
                                if (Array.isArray(ingreso.desgloseMensual) && ingreso.desgloseMensual.length > 0) {
                                    const cuotaRefRec = Math.max(...ingreso.desgloseMensual.map(d => Number(d.importe || 0)));
                                    const nombreConcepto = esPagoAnualRec
                                        ? 'Cuota de Mantenimiento'
                                        : (ingreso.concepto || 'Cuota de Mantenimiento');
                                    return ingreso.desgloseMensual.map(item => {
                                        const anioStr = item.año || item.anio || item.year || '';
                                        const esParcialItemRec = item.esParcial === true || (cuotaRefRec > 0 && Number(item.importe) < cuotaRefRec);
                                        const sufijoEstado = esParcialItemRec ? ' &middot; Pagado Parcial' : ' &middot; Pagado';
                                        return `<div class="preview-table-row">
                                            <span>${nombreConcepto} &middot; ${item.mes} ${anioStr}${sufijoEstado}</span>
                                            <span>${formatMoney(item.importe)}</span>
                                        </div>`;
                                    }).join('');
                                } else if (ingreso.mesesAplicados && ingreso.mesesAplicados.length > 0) {
                                    return ingreso.mesesAplicados.map(item => {
                                        const labelEstado = item.estado === 'pagado_completo' ? 'Pagado' : (item.estado === 'pagado_parcialmente' ? 'Pagado Parcial' : 'Abonado');
                                        return `<div class="preview-table-row">
                                            <span>${ingreso.concepto || 'Cuota de Mantenimiento'} &middot; ${item.nombreMes} ${item.año || item.anio} &middot; ${labelEstado}</span>
                                            <span>${formatMoney(item.montoAplicado)}</span>
                                        </div>`;
                                    }).join('');
                                } else {
                                    return `<div class="preview-table-row">
                                        <span>${ingreso.concepto || 'Pago registrado'}</span>
                                        <span>${formatMoney(ingreso.monto || 0)}</span>
                                    </div>`;
                                }
                            })()}
                        </div>
                    </div>
                    ${ingreso.observaciones ? `
                    <hr class="preview-divider" />
                    <div class="preview-block">
                        <h5>Observaciones</h5>
                        <p style="font-size:13px;color:#374151;white-space:pre-wrap;">${ingreso.observaciones}</p>
                    </div>` : ''}

                    <div class="preview-total">
                        <span>TOTAL</span>
                        <span class="preview-total-amount">${formatMoney(total)}</span>
                    </div>
                    <p class="preview-leyenda">(${totalLetras})</p>

                    <div class="preview-footer">
                        <div class="preview-firma-stack">
                            ${cfg.selloUrl ? `<img src="${cfg.selloUrl}" alt="Sello" class="preview-sello" />` : '<div class="preview-sello placeholder">Sello / Firma</div>'}
                            <div class="preview-firma">
                                <div class="preview-firma-line" />
                                <small>Firma y Sello</small>
                                <span>${cfg.administradorNombre || '---'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            const styles = `
                <style>
                * { box-sizing: border-box; }
                body { margin: 0; font-family: 'Inter', Arial, sans-serif; background: #f8fafc; }
                .receipt-preview { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 20px; color: #111827; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08); }
                .preview-row { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
                .preview-top img.preview-logo, .preview-top .preview-logo.placeholder { width: 120px; height: 80px; object-fit: contain; border: 1px dashed #cbd5e1; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #94a3b8; background: #f8fafc; }
                .preview-condo h4 { margin: 0; font-size: 16px; }
                .preview-condo p { margin: 4px 0 0; color: #4b5563; font-size: 13px; }
                .preview-right { text-align: right; }
                .preview-title { margin: 0; color: #1d4ed8; font-size: 20px; letter-spacing: 0.4px; }
                .preview-meta { display: flex; flex-direction: column; gap: 2px; color: #4b5563; font-size: 13px; margin-top: 6px; text-align: right; }
                .preview-divider { border: none; border-top: 1px solid #e5e7eb; margin: 16px 0; }
                .preview-block { text-align: left; }
                .preview-block h5 { margin: 0 0 6px; font-size: 14px; color: #1f2937; }
                .preview-block p { margin: 2px 0; font-size: 13px; color: #374151; }
                .preview-muted { color: #6b7280; font-size: 12px; }
                .preview-table { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
                .preview-table-header, .preview-table-row { display: grid; grid-template-columns: 1fr 140px; padding: 10px 12px; }
                .preview-table-header { background: #f1f5f9; font-weight: 700; font-size: 13px; }
                .preview-table-row { border-top: 1px solid #e5e7eb; font-size: 13px; }
                .preview-total { display: flex; justify-content: space-between; align-items: center; font-weight: 700; font-size: 16px; margin-top: 18px; }
                .preview-total-amount { color: #1d4ed8; font-size: 18px; }
                .preview-leyenda { font-style: italic; font-size: 12px; color: #374151; margin: 10px 0 20px; }
                .preview-footer { display: flex; justify-content: center; align-items: center; gap: 12px; }
                .preview-sello { width: 140px; height: 80px; object-fit: contain; border: 1px dashed #cbd5e1; border-radius: 6px; }
                .preview-sello.placeholder { display: flex; align-items: center; justify-content: center; color: #94a3b8; background: #f8fafc; }
                .preview-firma-stack { display: flex; flex-direction: column; align-items: center; gap: 10px; }
                .preview-firma { text-align: center; width: 260px; max-width: 100%; }
                .preview-firma-line { height: 1px; background: #1f2937; margin: 0 auto 6px; width: 220px; max-width: 100%; }
                .preview-firma small { color: #6b7280; display: block; }
                .preview-firma span { color: #1f2937; font-weight: 600; }
                @media print { body { background: white; } }
                </style>
            `;

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                setSendingStatus({ type: 'error', msg: 'No se pudo abrir ventana de descarga' });
                return;
            }

            printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8">${styles}</head><body>${receiptHtml}</body></html>`);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();

            setSendingStatus(null);
        } catch (error) {
            console.error('Error descargando recibo:', error);
            setSendingStatus({ type: 'error', msg: 'Error al descargar el recibo' });
        }
    };

    const handleSendByEmail = async (ingreso) => {
        try {
            setSendingStatus('loading');
            const email = historyResident?.email || historyResident?.correo;

            if (!email) {
                setSendingStatus({ type: 'error', msg: 'No hay correo registrado para esta casa' });
                return;
            }

            const payloadToSend = {
                email,
                fraccId: fraccionamientoId,
                receipt: ingreso,
                config: recibosConfig,
                casaInfo: { ...historyResident, fraccionamientoId }
            };

            if (!navigator.onLine) {
                pushToSyncQueue('sendReceiptEmail', payloadToSend);
                setSendingStatus({ type: 'success', msg: 'Guardado para envío offline' });
                setTimeout(() => setSendingStatus(null), 2000);
                return;
            }

            const sendReceiptEmail = httpsCallable(functions, 'sendReceiptEmail');

            const response = await sendReceiptEmail(payloadToSend);

            if (response.data.success) {
                setSendingStatus({ type: 'success', msg: 'Correo enviado exitosamente' });
                setTimeout(() => setSendingStatus(null), 2000);
            } else {
                setSendingStatus({ type: 'error', msg: response.data.message });
            }
        } catch (error) {
            console.error('Error enviando correo:', error);
            setSendingStatus({ type: 'error', msg: 'Error al enviar correo' });
        }
    };

    const handleExportToExcel = () => {
        try {
            const filteredData = historyIngresos.filter(ing =>
                (ing.concepto || '').toLowerCase().includes(historySearchTerm.toLowerCase()) ||
                (ing.referencia || '').toLowerCase().includes(historySearchTerm.toLowerCase()) ||
                (ing.metodoPago || '').toLowerCase().includes(historySearchTerm.toLowerCase())
            );

            const nombreResidente = historyResident.nombre_residente || historyResident.nombre || 'No registrado';
            const numeroCasa = historyResident.numero || historyResident.numeroCasa || historyResident.lote || 'Sin número';
            const cuotaConfigured = Number(financialConfig?.cuotaMensual || 375);

            const dataForExcel = [];

            filteredData.forEach(ingreso => {
                const dateObj = (ingreso.timestamp?.toDate ? ingreso.timestamp.toDate() : (ingreso.timestamp ? new Date(ingreso.timestamp) : null))
                    || (ingreso.fecha?.toDate ? ingreso.fecha.toDate() : new Date(ingreso.fecha || 0));
                const fechaLabel = dateObj ? dateObj.toLocaleDateString('es-MX') : 'N/A';
                const horaLabel = dateObj ? dateObj.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';

                const conceptoRaw = ingreso.concepto || '';
                const conceptoLower = (conceptoRaw || '').toLowerCase();
                const tipoLower = (ingreso.tipo || '').toLowerCase();
                const metodoLower = (ingreso.metodoPago || '').toLowerCase();
                const refLower = (ingreso.referencia || '').toLowerCase();

                const esImportado = refLower === 'historico_importado' || 
                                    ingreso.origen === 'importacion_historico' ||
                                    metodoLower.includes('importación');

                let esAbonoMovimiento = false;
                let isAdeudoRow = false;
                const esTesoreriaMovimiento = !esPartidaContableResidente(ingreso);

                if (esTesoreriaMovimiento) {
                    esAbonoMovimiento = false;
                } else if (esImportado) {
                    if (tipoLower === 'pago' || metodoLower.includes('importación histórica')) {
                        esAbonoMovimiento = true;
                    }
                } else {
                    const esPagoPorMetodo = (metodoLower && metodoLower !== 'registro_automatico' && metodoLower !== 'n/a') &&
                                            (metodoLower.includes('transferencia') || 
                                             metodoLower.includes('bancaria') || 
                                             metodoLower.includes('efectivo') || 
                                             metodoLower.includes('tarjeta') || 
                                             metodoLower.includes('importación'));

                    const esConceptoAbono = conceptoLower.includes('adeudos anteriores') || conceptoLower.includes('pago') || conceptoLower.includes('abono');

                    if (esPagoPorMetodo || (esConceptoAbono && metodoLower !== 'registro_automatico')) {
                        esAbonoMovimiento = true;
                    } else {
                        isAdeudoRow = tipoLower.includes('adeudo') || conceptoLower.includes('adeudo cuota') || conceptoLower.includes('adeudo historico') || conceptoLower.includes('adeudo extraordinaria');
                    }
                }

                const valCargo = (esAbonoMovimiento || esTesoreriaMovimiento) ? 0 : (ingreso.cargo || ingreso.monto || ingreso.montoOriginal || ingreso.cuotaMensual || (isAdeudoRow ? cuotaConfigured : 0));
                const valAbono = esAbonoMovimiento ? (ingreso.abono || ingreso.monto || ingreso.montoNeto || 0) : (esTesoreriaMovimiento ? (ingreso.monto || ingreso.montoNeto || 0) : 0);

                const esRegistroMensual = ingreso.esRegistroMensual === true;
                const esAguaPotable = ingreso.tipo === 'Consumo de Agua Potable M3' || 
                    (ingreso.concepto && (ingreso.concepto.toLowerCase().includes('agua potable') || ingreso.concepto.toLowerCase().includes('cea')));
                const isExtraordinaria = ingreso.tipo === 'extraordinaria' || 
                    (ingreso.referencia && (ingreso.referencia.includes('EXT-') || ingreso.referencia.includes('EXT'))) ||
                    (ingreso.concepto && (ingreso.concepto.toLowerCase().includes('extraordinaria') || ingreso.concepto.toLowerCase().includes('cuota extra')));

                let estadoResidente = ingreso.estadoResidente || ingreso.estado || '';
                let colorBandera = ingreso.colorBandera || 'verde';

                if (esRegistroMensual) {
                    if (!ingreso.estadoResidente) {
                        const esMoroso = (Number(ingreso.saldoFinal || 0) < 0);
                        estadoResidente = esMoroso ? 'CON ADEUDO' : 'AL CORRIENTE';
                    }
                    if (!ingreso.colorBandera) {
                        const esMoroso = (estadoResidente === 'CON ADEUDO') || (Number(ingreso.saldoFinal || 0) < 0);
                        colorBandera = esMoroso ? 'rojo' : 'verde';
                    }
                } else if (esAguaPotable) {
                    if (!estadoResidente) {
                        const esMoroso = ingreso.estadoPago === 'moroso' || 
                                         (Number(ingreso.montoNeto || ingreso.neto || 0) < 0) || 
                                         (ingreso.concepto && ingreso.concepto.toLowerCase().includes('adeudo'));
                        estadoResidente = esMoroso ? 'CON ADEUDO' : 'SIN ADEUDO';
                    }
                    if (!ingreso.colorBandera) {
                        const esMoroso = estadoResidente === 'CON ADEUDO' || 
                                         (Number(ingreso.montoNeto || ingreso.neto || 0) < 0);
                        colorBandera = esMoroso ? 'rojo' : 'verde';
                    }
                } else if (isExtraordinaria && !ingreso.estadoResidente) {
                    const esMoroso = ingreso.estadoPago === 'moroso' || 
                                     (Number(ingreso.montoNeto || 0) < 0) || 
                                     (ingreso.concepto && ingreso.concepto.toLowerCase().includes('adeudo'));
                    estadoResidente = esMoroso ? 'CON ADEUDO' : 'SIN ADEUDO';
                    colorBandera = esMoroso ? 'rojo' : 'verde';
                }

                const isSaldoFinal = conceptoLower.includes('saldo final de mes');
                const displayConcept = isSaldoFinal ? 'SALDO FINAL DE MES' : ((conceptoLower.includes('adeudos anteriores')) ? (conceptoRaw || 'N/A') : (conceptoRaw || 'N/A'));

                let referenciaDisplay = ingreso.referencia || 'N/A';
                const tieneRefProfesional = /^(REG|EXT|INT)-/.test(referenciaDisplay) || referenciaDisplay.includes('Agua-Potable');

                if (ingreso.esRegistroMensual && !tieneRefProfesional && dateObj) {
                    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const yyyy = String(dateObj.getFullYear());
                    referenciaDisplay = 'CAR-' + mm + '-' + yyyy;
                }

                let tipoLabel = '';
                try {
                    const mesesPermitidos = Number(historyResident?.mesesPermitidosGracia ?? financialConfig?.mesesPermitidos ?? financialConfig?.interesesMesesPermitidosDeuda ?? 1);
                    const mesesAdeudo = Number(historyResident?.mesesAdeudo ?? historyResident?.meses ?? 0);
                    const diaSanciones = Number(financialConfig?.sancionesInicioDia ?? financialConfig?.corteDia ?? 1);
                    const hoy = new Date();
                    const cumpleDias = hoy.getDate() >= diaSanciones;
                    const adeudoCasa = Number(historyResident?.adeudo ?? 0);
                    const condicionHistorico = (adeudoCasa > cuotaConfigured * mesesPermitidos) && (mesesAdeudo >= mesesPermitidos) && cumpleDias && esRegistroMensual;
                    
                    if (esTesoreriaMovimiento) tipoLabel = 'Tesorería';
                    else if (isSaldoFinal) tipoLabel = 'Saldo';
                    else if (esAguaPotable) tipoLabel = ingreso.tipo || 'Consumo de Agua Potable M3';
                    else if (condicionHistorico) tipoLabel = 'Adeudo Historico';
                    else if (isAdeudoRow) tipoLabel = 'Adeudo Cuota';
                    else tipoLabel = 'Normal';
                } catch (e) {
                    tipoLabel = esTesoreriaMovimiento ? 'Tesorería' : (isSaldoFinal ? 'Saldo' : (esAguaPotable ? (ingreso.tipo || 'Consumo de Agua Potable M3') : (isAdeudoRow ? 'Adeudo Cuota' : '')));
                }

                const emailLabel = ingreso.emailEnviado ? 'Enviado' : 'No enviado';

                if (esRegistroMensual) {
                    const mm = dateObj ? String(dateObj.getMonth() + 1).padStart(2, '0') : String(new Date().getMonth() + 1).padStart(2, '0');
                    const yyyy = dateObj ? String(dateObj.getFullYear()) : String(new Date().getFullYear());
                    const nombreMes = dateObj ? dateObj.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }).toUpperCase() : '';

                    const esMarcadoSoloSaldo = ingreso.esSoloSaldoFinal || ingreso.esMarcadoHistorial || (ingreso.concepto && ingreso.concepto.startsWith('SALDO FINAL DE MES'));
                    const esMorosoCargo = colorBandera === 'rojo';
                    const finalSaldoMonto = esMorosoCargo
                        ? Math.abs(ingreso.saldoFinal || 0)
                        : (ingreso.desglose?.saldoFavorFinal || ingreso.saldoFavorFinal || 0);
                    const estadoSaldoFinal = colorBandera === 'rojo' ? 'CON ADEUDO' : 'SIN ADEUDO';

                    const conceptoSaldoFinal = (esMarcadoSoloSaldo && ingreso.concepto) ? ingreso.concepto : 'SALDO FINAL DE MES';
                    const refSaldoFinal = ingreso.referencia || 'SAL-' + mm + '-' + yyyy;

                    if (esMarcadoSoloSaldo) {
                        // Agregar fila de Saldo Final (si es saldo inicial importado)
                        dataForExcel.push({
                            'Fecha': fechaLabel,
                            'Hora': horaLabel,
                            'Concepto': conceptoSaldoFinal,
                            'Tipo': 'Saldo',
                            'Método de Pago': 'registro_automatico',
                            'Referencia': refSaldoFinal,
                            'Cargo / Neto': esMorosoCargo ? finalSaldoMonto : 0,
                            'Abono / Pagado': !esMorosoCargo ? finalSaldoMonto : 0,
                            'Email': 'N/A',
                            'Estado': estadoSaldoFinal
                        });
                    } else {
                        // Agregar fila de Mantenimiento base
                        dataForExcel.push({
                            'Fecha': fechaLabel,
                            'Hora': horaLabel,
                            'Concepto': displayConcept,
                            'Tipo': tipoLabel,
                            'Método de Pago': ingreso.metodoPago === 'saldo_favor' ? 'Saldo a Favor' : (ingreso.metodoPago === 'registro_automatico' ? 'Registro Automático' : (ingreso.metodoPago || 'N/A')),
                            'Referencia': referenciaDisplay,
                            'Cargo / Neto': valCargo,
                            'Abono / Pagado': valAbono,
                            'Email': emailLabel,
                            'Estado': esTesoreriaMovimiento ? 'Ingreso de Tesorería' : estadoResidente
                        });
                    }

                    // Agregar fila de extraordinaria (si existe) deduplicada del array
                    if (ingreso.cuotasExtraniordinarias && ingreso.cuotasExtraniordinarias.length > 0) {
                        const descripcionesVistas = new Set();
                        const cuotasFiltradas = [];
                        ingreso.cuotasExtraniordinarias.forEach(c => {
                            const descNorm = c.descripcion.toLowerCase()
                                .replace(/cuota\s+extra(ordinaria)?[:\s]*/gi, '')
                                .replace(/\*/g, '')
                                .replace(/[-_]/g, '')
                                .trim();
                            if (!descripcionesVistas.has(descNorm)) {
                                descripcionesVistas.add(descNorm);
                                cuotasFiltradas.push(c);
                            }
                        });

                        cuotasFiltradas.forEach(cuotaExt => {
                            const dateObjExtra = (cuotaExt.fechaCargo?.toDate ? cuotaExt.fechaCargo.toDate() : (cuotaExt.fechaCargo ? new Date(cuotaExt.fechaCargo) : null))
                                || (cuotaExt.fechaCreacion?.toDate ? cuotaExt.fechaCreacion.toDate() : (cuotaExt.fechaCreacion ? new Date(cuotaExt.fechaCreacion) : null))
                                || (cuotaExt.fecha?.toDate ? cuotaExt.fecha.toDate() : (cuotaExt.fecha ? new Date(cuotaExt.fecha) : null))
                                || dateObj;
                            const dateLabelExtra = dateObjExtra ? dateObjExtra.toLocaleDateString('es-MX') : fechaLabel;
                            const timeLabelExtra = dateObjExtra ? dateObjExtra.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : horaLabel;

                            dataForExcel.push({
                                'Fecha': dateLabelExtra,
                                'Hora': timeLabelExtra,
                                'Concepto': `Adeudo Cuota Extraordinaria "${cuotaExt.descripcion}" ${nombreMes}`,
                                'Tipo': 'Adeudo Cuota Extraordinaria',
                                'Método de Pago': 'registro_automatico',
                                'Referencia': `EXT-${mm}-${yyyy}`,
                                'Cargo / Neto': cuotaExt.monto || 0,
                                'Abono / Pagado': 0,
                                'Email': 'N/A',
                                'Estado': 'CON ADEUDO'
                            });
                        });
                    }
                } else {
                    dataForExcel.push({
                        'Fecha': fechaLabel,
                        'Hora': horaLabel,
                        'Concepto': displayConcept,
                        'Tipo': tipoLabel,
                        'Método de Pago': ingreso.metodoPago === 'saldo_favor' ? 'Saldo a Favor' : (ingreso.metodoPago === 'registro_automatico' ? 'Registro Automático' : (ingreso.metodoPago || 'N/A')),
                        'Referencia': referenciaDisplay,
                        'Cargo / Neto': valCargo,
                        'Abono / Pagado': valAbono,
                        'Email': emailLabel,
                        'Estado': estadoResidente
                    });
                }
            });

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet([]);

            // Fila 1: Título descriptivo
            XLSX.utils.sheet_add_aoa(worksheet, [[
                `Historial de Pagos - ${nombreResidente} - Casa ${numeroCasa}`
            ]], { origin: 'A1' });

            // Fila 2: Encabezados de columna y Fila 3+: Datos
            XLSX.utils.sheet_add_json(worksheet, dataForExcel, { origin: 'A2', skipHeader: false });

            XLSX.utils.book_append_sheet(workbook, worksheet, 'Historial Pagos');

            // Ajustar ancho de columnas (10 columnas: A a J)
            worksheet['!cols'] = [
                { wch: 12 }, // Fecha
                { wch: 10 }, // Hora
                { wch: 35 }, // Concepto
                { wch: 25 }, // Tipo
                { wch: 20 }, // Método de Pago
                { wch: 18 }, // Referencia
                { wch: 15 }, // Cargo / Neto
                { wch: 15 }, // Abono / Pagado
                { wch: 12 }, // Email
                { wch: 15 }, // Estado
            ];

            // Fusionar celdas para el título (A1:J1)
            worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }];

            // Post-procesar columnas Cargo/Abono: aplicar formato de moneda
            try {
                const currencyFormat = '$#,##0.00;[Red]-$#,##0.00';
                for (let i = 0; i < dataForExcel.length; i++) {
                    const row = i + 2; // zero-based r = i + 2 (los datos arrancan en la fila 3)
                    const cargoCell = XLSX.utils.encode_cell({ c: 6, r: row }); // Columna G (c: 6)
                    const abonoCell = XLSX.utils.encode_cell({ c: 7, r: row }); // Columna H (c: 7)

                    if (worksheet[cargoCell]) {
                        worksheet[cargoCell].t = 'n';
                        worksheet[cargoCell].z = currencyFormat;
                    }
                    if (worksheet[abonoCell]) {
                        worksheet[abonoCell].t = 'n';
                        worksheet[abonoCell].z = currencyFormat;
                    }
                }
            } catch (err) {
                console.error('Error aplicando formatos al excel:', err);
            }

            XLSX.writeFile(workbook, `Historial_Pagos_${nombreResidente.replace(/\s+/g, '_')}_Casa_${numeroCasa.replace(/\s+/g, '_')}.xlsx`);
        } catch (error) {
            console.error('Error exportando historial a Excel:', error);
        }
    };

    const handleApplyCharges = async () => {
        if (!fraccionamientoId || applyingCharges) return;

        if (chargesState?.inProgress) {
            setApplyChargesMessage({ type: 'info', text: 'Ya hay una ejecución en progreso. Espera a que finalice.' });
            return;
        }
        if (chargesState?.lastRunDate === todayKey) {
            setApplyChargesMessage({ type: 'info', text: 'Los cargos ya se aplicaron hoy. Vuelve a intentarlo mañana o usa force solo para soporte.' });
            return;
        }

        const confirmed = window.confirm('Aplicar los cargos financieros ahora? Solo se permite una ejecución por día.');
        if (!confirmed) return;

        try {
            setApplyingCharges(true);
            setApplyChargesMessage({ type: 'progress', text: 'Aplicando cargos, no cierres la página…' });

            const applyCharges = httpsCallable(functions, 'applyFinancialCharges');
            const response = await applyCharges({ fraccionamientoId });
            const data = response?.data || {};

            if (data.skippedReason === 'before-cutoff') {
                setApplyChargesMessage({ type: 'warning', text: 'Aún no llega el día de corte. Se omitió la ejecución.' });
                return;
            }
            if (data.skippedReason === 'already-run-today') {
                setApplyChargesMessage({ type: 'info', text: 'Ya se había aplicado hoy. No se duplicaron cargos.' });
                return;
            }
            if (data.skippedReason === 'in-progress') {
                setApplyChargesMessage({ type: 'info', text: 'Hay otro proceso corriendo. Intenta en unos minutos.' });
                return;
            }

            setApplyChargesMessage({
                type: 'success',
                text: `Cargos aplicados correctamente (${data.applied || 0}). Revisa movimientos y saldos para confirmar.`,
            });
        } catch (err) {
            console.error('ResidentStatusPage: error al aplicar cargos', err);
            setApplyChargesMessage({ type: 'error', text: err?.message || 'No se pudieron aplicar los cargos.' });
        } finally {
            setApplyingCharges(false);
        }
    };

    return (
        <div className="departamentos-page">
            <div className="departamentos-container">
                <header className="departamentos-header">
                    <div className="departamentos-header-icon">
                        <FaUsers />
                    </div>
                    <div>
                        <h1>{config.title}</h1>
                        <p>{config.description}</p>
                    </div>
                    <button
                        type="button"
                        className="departamentos-back-link"
                        onClick={() => {
                            if (location.state?.originalSearch !== undefined) {
                                navigate('/administracion/departamentos/residentes', {
                                    state: { search: location.state.originalSearch }
                                });
                            } else {
                                navigate(-1);
                            }
                        }}
                    >
                        <FaChevronLeft /> Volver
                    </button>
                </header>

                <section className="finanzas-control-card">
                    <div className="finanzas-control-text">
                        <div className="finanzas-control-top">
                            <div>
                                <h3>Cobranza mensual</h3>
                            </div>
                            <div className="finanzas-metrics">
                                <div className="finanzas-chip ok">Al corriente: <strong>{residentSummary.corrientesCount}</strong></div>
                                <div className="finanzas-chip alert">Morosos: <strong>{residentSummary.morososCount}</strong></div>
                                <div className="finanzas-chip ghost">Total casas: <strong>{residentSummary.total}</strong></div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className={`status-detail-panel ${config.accentClass}`}>
                    <header className="status-detail-header">
                        <div className="status-detail-header-left">
                            <small>Mostrando {filteredResidents.length} de {statusResidents.length} residente(s)</small>
                            <h3>{config.title}</h3>
                            <p>{config.description}</p>
                        </div>
                        {isMorosoView && (
                            <div className="status-detail-header-actions">
                                <button
                                    type="button"
                                    className="btn-reporte-ejecutivo-zen"
                                    onClick={() => navigate('/administracion/departamentos/residentes/morosos/reporte', {
                                        state: { originalSearch: location.state?.originalSearch || searchTerm }
                                    })}
                                    title="Configurar y descargar reporte ejecutivo de morosidad"
                                >
                                    <FaChartBar className="me-1" />
                                    <span>Reporte Ejecutivo</span>
                                </button>
                            </div>
                        )}
                        <div className="status-filter-controls">
                            <div className="residentes-filter-input-wrapper">
                                <FaSearch className="residentes-filter-search-icon" />
                                <input
                                    className="residentes-filter-input"
                                    type="text"
                                    placeholder="Buscar por número de casa..."
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    aria-label="Filtrar residentes"
                                />
                            </div>
                            <span className="residentes-filter-helper-text">
                                Filtra por el **número de casa** del residente.
                            </span>
                        </div>
                    </header>

                    {isMorosoView && (
                        <div className="resident-summary-metrics">
                            <div className="resident-summary-pill">
                                Total adeudo: <strong>{currencyFormatter.format(totalAdeudo)}</strong>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <p className="status-empty">Cargando residentes...</p>
                    ) : error ? (
                        <p className="status-resident-error">{error}</p>
                    ) : filteredResidents.length === 0 ? (
                        <p className="status-empty">No se encontraron residentes con ese criterio.</p>
                    ) : (
                        <div className="status-detail-grid">
                            {filteredResidents.map((resident) => {
                                const casaLabel = pickFirstString([
                                    resident.numero,
                                    resident.numeroCasa,
                                    resident.lote,
                                ]) || 'Sin número';
                                const residentName = pickFirstString([
                                    resident.nombre_residente,
                                    resident.nombre,
                                    resident.nombreTitular,
                                    resident.titular,
                                    resident.propietario,
                                    resident.arrendador,
                                    resident.nombrePropietario,
                                    resident.nombreCompleto,
                                ]) || 'Nombre no registrado';
                                const residentRole = pickFirstString([
                                    resident.rol,
                                    resident.rolResidente,
                                    resident.tipo,
                                    resident.tipoResidente,
                                    resident.estatus,
                                    resident.relacion,
                                    resident.perfil,
                                ]);
                                const telefono = pickFirstString([
                                    resident.telefono,
                                    resident.telefono_principal,
                                    resident.telefonoPrincipal,
                                    resident.telefono1,
                                    resident.telefono2,
                                    resident.telefonoSecundario,
                                    resident.celular,
                                ]);
                                const correo = pickFirstString([
                                    resident.email,
                                    resident.correo,
                                    resident.correoElectronico,
                                ]);
                                const ubicacion = pickFirstString([
                                    resident.ubicacion,
                                    resident.direccionCompleta,
                                    resident.direccion,
                                ]);
                                const cityPart = pickFirstString([
                                    resident.ciudad,
                                    resident.ciudadResidencia,
                                    resident.municipio,
                                    resident.localidad,
                                ]);
                                const statePart = pickFirstString([
                                    resident.estado,
                                    resident.estadoResidencia,
                                    resident.state,
                                ]);
                                const countryPart = pickFirstString([
                                    resident.pais,
                                    resident.country,
                                    resident.paisResidencia,
                                ]);
                                let locationLabel = [cityPart, statePart, countryPart].filter(Boolean).join(', ');
                                if (!locationLabel) {
                                    locationLabel = ubicacion || 'Sin ubicación registrada';
                                }
                                const idNorm = String(resident.id || '').trim().toUpperCase();
                                const numNorm = String(resident.numero || resident.numeroCasa || casaLabel || '').trim().toUpperCase();
                                const adeudoAgua = Number(resident.adeudoAgua || resident.montoAguaPendiente || 0);
                                const adeudoIntereses = Number(resident.adeudoIntereses ?? 0);
                                const saldoFavor = Number(resident.saldoFavor ?? 0);
                                const adeudoMultas = (mapMultasPendientes[idNorm] || 0) || (mapMultasPendientes[numNorm] || 0) || Number(resident.adeudoMultas || resident.sancionesAdmin || 0);
                                const adeudoExtras = (mapExtrasPendientes[idNorm] || 0) || (mapExtrasPendientes[numNorm] || 0) || Number(resident.cuotasExtras || resident.adeudoExtras || 0);

                                const adeudoBaseNeto = Number(resident.adeudoCapital ?? 0);
                                const saldoFavorNeto = saldoFavor;
                                
                                const totalAPagarFinDeMesAdmin = Number(resident.adeudo || 0);
                                const totalAFavorAdmin = Math.max(0, saldoFavorNeto - totalAPagarFinDeMesAdmin);
                                
                                const adeudoNeto = totalAPagarFinDeMesAdmin;
                                const hasAdeudo = adeudoNeto > 0;
                                const totalRealCobro = totalAPagarFinDeMesAdmin;

                                // Priorizar num_vehiculos de Firebase, luego buscar en campos legacy
                                let rawVehiclesCount = resident.num_vehiculos !== undefined && resident.num_vehiculos !== null
                                    ? resident.num_vehiculos
                                    : pickVehicleMetric(resident);
                                const hasVehicleMetric = rawVehiclesCount !== undefined;
                                const vehiclesCount = hasVehicleMetric ? toNonNegativeInteger(rawVehiclesCount) : null;


                                // Obtener tag si existe
                                const tagValue = resident.tag !== undefined && resident.tag !== null ? resident.tag : null;

                                const rawMascotas = pickFirstValue(resident, PET_DATA_KEYS);
                                const hasMascotasMetric = rawMascotas !== undefined;
                                const mascotasLabel = hasMascotasMetric ? buildMascotasLabel(rawMascotas) : null;
                                const mascotasLabelIndicatesNone = typeof mascotasLabel === 'string' && /sin\s+mascotas/i.test(mascotasLabel);

                                const lastPaymentRaw = pickFirstValue(resident, [
                                    'ultimoPago',
                                    'ultimo_pago',
                                    'fechaUltimoPago',
                                    'fecha_ultimo_pago',
                                    'ultimoPagoFecha',
                                    'ultimo_pago_fecha',
                                    'lastPayment',
                                    'lastPaymentDate',
                                    'ultimaFechaPago',
                                ]);
                                const nextPaymentRaw = pickFirstValue(resident, [
                                    'proximoPago',
                                    'proximo_pago',
                                    'fechaProximoPago',
                                    'fecha_proximo_pago',
                                    'proximoPagoFecha',
                                    'fechaProximoPagoISO',
                                    'nextPaymentDate',
                                    'fechaVencimiento',
                                    'vencimiento',
                                ]);
                                const lastPaymentDate = toJsDate(lastPaymentRaw);
                                let nextPaymentDate = toJsDate(nextPaymentRaw);
                                if (!nextPaymentDate && billingCutoffDay) {
                                    nextPaymentDate = computeNextCutoffDateFromDay(billingCutoffDay);
                                }
                                const lastPaymentLabel = formatDateLabel(lastPaymentDate);
                                const nextPaymentLabel = formatDateLabel(nextPaymentDate);
                                let displayedNextPaymentLabel = nextPaymentLabel;
                                if (!displayedNextPaymentLabel) {
                                    const fallbackDate = computeNextCutoffDateFromDay(billingCutoffDay ?? (new Date().getDate()));
                                    displayedNextPaymentLabel = fallbackDate ? formatDateLabel(fallbackDate) : 'Por definir';
                                }

                                let displayedLastPaymentLabel = lastPaymentLabel;
                                let lastPaymentVariant = isMorosoView ? 'alert' : 'success';
                                let lastPaymentMuted = false;
                                if (!displayedLastPaymentLabel) {
                                    const fallback = new Date();
                                    fallback.setMonth(fallback.getMonth() - (isMorosoView ? 2 : 1));
                                    displayedLastPaymentLabel = formatDateLabel(fallback) || 'No registrado';
                                    lastPaymentMuted = true;
                                }
                                const delayDays = computeDelayDays(resident);
                                const statusMessage = isMorosoView
                                    ? (delayDays > 0 ? `${delayDays} día${delayDays === 1 ? '' : 's'} de retraso` : 'Con adeudo pendiente')
                                    : 'Al corriente';
                                const statusIconElement = isMorosoView ? <FaExclamationTriangle /> : <FaCheckCircle />;

                                let vehiclesDisplay = 'Sin dato registrado';
                                if (hasVehicleMetric) {
                                    if (vehiclesCount !== null) {
                                        vehiclesDisplay = `${vehiclesCount} vehículo${vehiclesCount === 1 ? '' : 's'}`;
                                        // Agregar tag si existe
                                        if (tagValue !== null) {
                                            vehiclesDisplay += ` • Tag: ${tagValue}`;
                                        }
                                    } else if (typeof rawVehiclesCount === 'string') {
                                        const label = rawVehiclesCount.trim();
                                        vehiclesDisplay = label.length > 0 ? label : vehiclesDisplay;
                                        // Agregar tag si existe
                                        if (tagValue !== null) {
                                            vehiclesDisplay += ` • Tag: ${tagValue}`;
                                        }
                                    }
                                } else if (tagValue !== null) {
                                    // Si no hay datos de vehículos pero sí hay tag, mostrar solo tag
                                    vehiclesDisplay = `Tag: ${tagValue}`;
                                }

                                const nextPaymentValue = displayedNextPaymentLabel
                                    ? `Próximo: ${displayedNextPaymentLabel}`
                                    : 'Próximo: Por definir';

                                const cardKey = (resident.id || casaLabel || '').toString();
                                const idNormCasa = String(resident.id || '').trim().toUpperCase();
                                const numNormCasa = String(resident.numero || resident.numeroCasa || casaLabel || '').trim().toUpperCase();

                                let periodosAguaCasa = mapPeriodosAguaPendientes[idNormCasa] || mapPeriodosAguaPendientes[numNormCasa] || [];
                                if (adeudoAgua > 0 && periodosAguaCasa.length === 0) {
                                    const periodoFallback = resident.ultimoMesAguaVariable 
                                        ? formatPeriodoAguaLabel(null, null, null, null, `AGUA-CASA-${resident.ultimoMesAguaVariable}`)
                                        : 'Período pendiente';
                                    periodosAguaCasa = [{
                                        id: 'fallback-agua',
                                        periodo: periodoFallback,
                                        monto: adeudoAgua
                                    }];
                                }

                                const itemsAdeudosActivos = [];
                                if (adeudoBaseNeto > 0) {
                                    itemsAdeudosActivos.push({
                                        key: 'deuda-mant',
                                        icon: <FaHome style={{ color: '#f59e0b' }} />,
                                        title: 'Adeudo Mant.',
                                        value: currencyFormatter.format(adeudoBaseNeto),
                                        variant: 'financial-row',
                                        montoVal: adeudoBaseNeto,
                                        hasBreakdown: true,
                                        breakdownLabel: 'Ver meses',
                                        breakdownVariant: 'mantto',
                                        onBreakdown: (e) => {
                                            e.stopPropagation();
                                            handleOpenDesgloseModal(resident, 'mantenimiento');
                                        }
                                    });
                                }
                                if (adeudoIntereses > 0) {
                                    itemsAdeudosActivos.push({
                                        key: 'deuda-interes',
                                        icon: <FaExclamationTriangle style={{ color: '#ef4444' }} />,
                                        title: 'Adeudo Intereses',
                                        value: currencyFormatter.format(adeudoIntereses),
                                        variant: 'financial-row',
                                        montoVal: adeudoIntereses
                                    });
                                }
                                if (adeudoAgua > 0) {
                                    itemsAdeudosActivos.push({
                                        key: 'deuda-agua',
                                        icon: <FaTint style={{ color: '#0284c7' }} />,
                                        title: 'Adeudo Agua',
                                        value: currencyFormatter.format(adeudoAgua),
                                        variant: 'financial-row',
                                        montoVal: adeudoAgua,
                                        periodosAgua: periodosAguaCasa,
                                        casaKey: cardKey
                                    });
                                }
                                if (adeudoMultas > 0) {
                                    itemsAdeudosActivos.push({
                                        key: 'deuda-multas',
                                        icon: <FaGavel style={{ color: '#ec4899' }} />,
                                        title: 'Sanciones Admin.',
                                        value: currencyFormatter.format(adeudoMultas),
                                        variant: 'financial-row',
                                        montoVal: adeudoMultas,
                                        hasBreakdown: true,
                                        breakdownLabel: 'Ver sanciones',
                                        breakdownVariant: 'multas',
                                        onBreakdown: (e) => {
                                            e.stopPropagation();
                                            handleOpenDesgloseModal(resident, 'sanciones');
                                        }
                                    });
                                }
                                if (adeudoExtras > 0) {
                                    itemsAdeudosActivos.push({
                                        key: 'deuda-extras',
                                        icon: <FaCalendarAlt style={{ color: '#f59e0b' }} />,
                                        title: 'Cuotas Extras',
                                        value: currencyFormatter.format(adeudoExtras),
                                        variant: 'financial-row',
                                        montoVal: adeudoExtras,
                                        hasBreakdown: true,
                                        breakdownLabel: 'Ver cuotas',
                                        breakdownVariant: 'extras',
                                        onBreakdown: (e) => {
                                            e.stopPropagation();
                                            handleOpenDesgloseModal(resident, 'extraordinarias');
                                        }
                                    });
                                }

                                const infoColumnLeft = [
                                    {
                                        key: 'phone',
                                        icon: <FaPhone />,
                                        title: 'Teléfono',
                                        value: telefono || 'Sin teléfono registrado',
                                        muted: !telefono,
                                    },
                                    {
                                        key: 'email',
                                        icon: <FaEnvelope />,
                                        title: 'Correo',
                                        value: correo || 'Sin correo registrado',
                                        muted: !correo,
                                    },
                                    {
                                        key: 'location',
                                        icon: <FaMapMarkerAlt />,
                                        title: 'Origen',
                                        value: locationLabel,
                                        muted: !locationLabel || locationLabel === 'Sin ubicación registrada',
                                    },
                                ];

                                const emergenciaNombre = resident.emergencia?.nombre || '';
                                const emergenciaTelefono = resident.emergencia?.telefono || '';
                                const tieneEmergencia = emergenciaNombre || emergenciaTelefono;
                                const valorEmergencia = tieneEmergencia
                                    ? `${emergenciaNombre}${emergenciaTelefono ? ` • ${emergenciaTelefono}` : ''}`
                                    : 'Sin contacto registrado';

                                if (itemsAdeudosActivos.length > 2) {
                                    infoColumnLeft.push({
                                        key: 'contacto-emergencia',
                                        icon: <FaExclamationCircle style={{ color: '#ef4444' }} />,
                                        title: 'Contacto Emer.',
                                        value: valorEmergencia,
                                        muted: !tieneEmergencia,
                                    });
                                }

                                const infoColumnRight = [];
                                const isExpanded = !!expandedCards[cardKey];

                                if (itemsAdeudosActivos.length > 2) {
                                    // Ordenar de mayor a menor monto
                                    itemsAdeudosActivos.sort((a, b) => b.montoVal - a.montoVal);
                                    
                                    if (!isExpanded) {
                                        // En modo colapsado, mostramos los 2 de mayor valor
                                        infoColumnRight.push(itemsAdeudosActivos[0]);
                                        infoColumnRight.push(itemsAdeudosActivos[1]);
                                        
                                        // Mostrar Total a Pagar al inicio (en modo colapsado)
                                        if (totalAPagarFinDeMesAdmin > 0) {
                                            infoColumnRight.push({
                                                key: 'total-adeudo',
                                                icon: <FaDollarSign style={{ color: '#b91c1c' }} />,
                                                title: 'Total a Pagar',
                                                value: currencyFormatter.format(totalAPagarFinDeMesAdmin),
                                                variant: 'danger-highlight',
                                            });
                                        }

                                        // Botón para expandir
                                        infoColumnRight.push({
                                            key: 'toggle-expand',
                                            icon: <FaChevronDown style={{ color: '#6b21a8' }} />,
                                            title: 'Ver más',
                                            value: `Detalle de adeudos (+${itemsAdeudosActivos.length - 2})`,
                                            variant: 'financial-row',
                                            onClick: () => setExpandedCards(prev => ({ ...prev, [cardKey]: true }))
                                        });
                                    } else {
                                        // En modo expandido, mostramos todos los adeudos activos
                                        itemsAdeudosActivos.forEach(item => {
                                            infoColumnRight.push(item);
                                        });

                                        // Total a Pagar al final
                                        if (totalAPagarFinDeMesAdmin > 0) {
                                            infoColumnRight.push({
                                                key: 'total-adeudo',
                                                icon: <FaDollarSign style={{ color: '#b91c1c' }} />,
                                                title: 'Total a Pagar',
                                                value: currencyFormatter.format(totalAPagarFinDeMesAdmin),
                                                variant: 'danger-highlight',
                                            });
                                        }

                                        // Botón para colapsar
                                        infoColumnRight.push({
                                            key: 'toggle-collapse',
                                            icon: <FaChevronUp style={{ color: '#6b21a8' }} />,
                                            title: 'Ver menos',
                                            value: 'Ocultar detalle',
                                            variant: 'financial-row',
                                            onClick: () => setExpandedCards(prev => ({ ...prev, [cardKey]: false }))
                                        });
                                    }
                                } else {
                                    // Si hay 2 o menos adeudos activos, se muestra normal sin desplegable
                                    itemsAdeudosActivos.forEach(item => {
                                        infoColumnRight.push(item);
                                    });

                                    // Ocultar saldo a favor si hay adeudo neto a pagar
                                    const mostrarSaldoFavorAdmin = saldoFavorNeto > 0 && totalAPagarFinDeMesAdmin <= 0;
                                    if (mostrarSaldoFavorAdmin) {
                                        infoColumnRight.push({
                                            key: 'saldo-favor',
                                            icon: <FaDollarSign style={{ color: '#10b981' }} />,
                                            title: 'Saldo a favor',
                                            value: currencyFormatter.format(saldoFavorNeto),
                                            variant: 'financial-row',
                                        });
                                    }
                                    
                                    if (totalAPagarFinDeMesAdmin === 0 && totalAFavorAdmin === 0) {
                                        infoColumnRight.push({
                                            key: 'sin-adeudos',
                                            icon: <FaCheckCircle style={{ color: '#10b981' }} />,
                                            title: 'Estatus',
                                            value: 'Sin adeudos pendientes',
                                            variant: 'financial-row',
                                        });
                                    }

                                    if (totalAPagarFinDeMesAdmin > 0) {
                                        infoColumnRight.push({
                                            key: 'total-adeudo',
                                            icon: <FaDollarSign style={{ color: '#b91c1c' }} />,
                                            title: 'Total a Pagar',
                                            value: currencyFormatter.format(totalAPagarFinDeMesAdmin),
                                            variant: 'danger-highlight',
                                        });
                                    } else if (totalAFavorAdmin > 0) {
                                        infoColumnRight.push({
                                            key: 'total-saldo',
                                            icon: <FaDollarSign style={{ color: '#047857' }} />,
                                            title: 'Total a Favor',
                                            value: currencyFormatter.format(totalAFavorAdmin),
                                            variant: 'success-highlight',
                                        });
                                    }
                                }

                                const infoColumns = [infoColumnLeft, infoColumnRight];

                                const primaryActions = Array.isArray(config.primaryActions) ? config.primaryActions : [];
                                const isAdministrador = userData?.rol === 'superadmin' || userData?.rol === 'platformadmin' || userData?.rol === 'admin';
                                let secondaryActions = Array.isArray(config.secondaryActions) ? [...config.secondaryActions] : [];
                                const mostrarAjusteSaldos = modulos?.boton_ajuste_saldos_admin !== false;
                                if (isAdministrador && mostrarAjusteSaldos) {
                                    secondaryActions.push({
                                        label: 'Ajuste de Saldos',
                                        variant: 'ghost',
                                        icon: FaDollarSign
                                    });
                                }

                                const getResidentKey = () => {
                                    return (resident.id || casaLabel || '').toString();
                                };

                                const openProfileModal = () => {
                                    setSelectedResident(resident);
                                };

                                const goToHistory = () => {
                                     openResidentHistory(resident);
                                 };

                                const goToRecibo = () => {
                                    setPaymentResident(resident);
                                    setIsPaymentModalOpen(true);
                                };

                                const contactResident = () => {
                                    const phone = telefono && telefono.replace(/\s+/g, '');
                                    if (phone) {
                                        window.location.href = `tel:${phone}`;
                                        return;
                                    }
                                    if (correo) {
                                        const subject = `Contacto administración - Vivienda ${casaLabel}`;
                                        window.location.href = `mailto:${correo}?subject=${encodeURIComponent(subject)}`;
                                    }
                                };

                                return (
                                    <article key={resident.id || casaLabel} className="resident-detail-card">
                                        <header className={`resident-card-top ${isMorosoView ? 'alert' : 'ok'}`}>
                                            <div className="resident-top-left">
                                                <div className="resident-number-circle">
                                                    <span>{casaLabel}</span>
                                                </div>
                                                <div className="resident-top-details">
                                                    <h4>{residentName}</h4>
                                                </div>
                                            </div>
                                            <div className="resident-top-right" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                {saldoFavor > 0 && (
                                                    <div style={{
                                                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                                                        color: '#047857',
                                                        border: '1px solid rgba(16, 185, 129, 0.25)',
                                                        borderRadius: '8px',
                                                        padding: '6px 12px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 700,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        Saldo a favor: {currencyFormatter.format(saldoFavor)}
                                                    </div>
                                                )}
                                                <div className="resident-header-icons" style={{ display: 'flex', gap: '6px' }}>
                                                    {adeudoAgua > 0 && (
                                                        <span title={`Adeudo de Agua pendiente: ${currencyFormatter.format(adeudoAgua)}`} style={{ color: '#0284c7', display: 'inline-flex', alignItems: 'center' }}>
                                                            <FaTint style={{ fontSize: '1.1rem' }} />
                                                        </span>
                                                    )}
                                                    {adeudoBaseNeto > 0 && (
                                                        <span title={`Adeudo de Mantenimiento pendiente: ${currencyFormatter.format(adeudoBaseNeto)}`} style={{ color: '#f59e0b', display: 'inline-flex', alignItems: 'center' }}>
                                                            <FaHome style={{ fontSize: '1.15rem' }} />
                                                        </span>
                                                    )}
                                                    {adeudoIntereses > 0 && (
                                                        <span title={`Adeudo de Intereses/Multas pendiente: ${currencyFormatter.format(adeudoIntereses)}`} style={{ color: '#ef4444', display: 'inline-flex', alignItems: 'center' }}>
                                                            <FaExclamationTriangle style={{ fontSize: '1.05rem' }} />
                                                        </span>
                                                    )}
                                                    {totalRealCobro === 0 && (
                                                        <span title="Al corriente y sin deudas" style={{ color: '#10b981', display: 'inline-flex', alignItems: 'center' }}>
                                                            <FaCheckCircle style={{ fontSize: '1.15rem' }} />
                                                        </span>
                                                    )}
                                                </div>
                                                <span className={`residente-status-badge residente-pill ${isMorosoView ? 'moroso' : 'corriente'}`} style={{ position: 'static' }}>
                                                    {isMorosoView ? 'Con adeudo' : 'Al corriente'}
                                                </span>
                                            </div>
                                        </header>
                                        <div className="resident-detail-body">
                                            <div className="resident-info-columns">
                                                {infoColumns.map((column, columnIndex) => (
                                                    <div key={`${resident.id || casaLabel}-col-${columnIndex}`} className="resident-info-column">
                                                        {column.map(renderInfoRow)}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="resident-dates-compact-row">
                                                <div className="compact-date-badge last-payment">
                                                    <FaCalendarCheck />
                                                    <span>Último pago: <strong>{displayedLastPaymentLabel}</strong></span>
                                                </div>
                                                <div className="compact-date-badge next-payment">
                                                    <FaCalendarTimes />
                                                    <span>Próximo pago: <strong>{displayedNextPaymentLabel}</strong></span>
                                                </div>
                                            </div>
                                        </div>
                                        <footer className="resident-detail-actions">
                                            {primaryActions.length > 0 && (
                                                <div className="resident-action-row primary-row">
                                                    {primaryActions.map((action) => {
                                                        const ActionIcon = action.icon;
                                                        return (
                                                            <button
                                                                key={action.label}
                                                                type="button"
                                                                className={`resident-action ${action.variant}`}
                                                                onClick={() => {
                                                                    const label = (action.label || '').toLowerCase();
                                                                    if (label.includes('perfil')) { openProfileModal(); return; }
                                                                    if (label.includes('contact')) { contactResident(); return; }
                                                                }}
                                                            >
                                                                {ActionIcon ? <ActionIcon aria-hidden="true" /> : null}
                                                                <span>{action.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {secondaryActions.length > 0 && (
                                                <div className="resident-action-row secondary-row">
                                                    {secondaryActions.map((action) => {
                                                        const ActionIcon = action.icon;
                                                        const label = (action.label || '').toLowerCase();
                                                        const isPaymentAction = label.includes('recibo') || label.includes('ticket');
                                                        const isHistoryAction = label.includes('historial');
                                                        const isSaldosAction = label.includes('saldo');
                                                        const isDisabled = (isPaymentAction || isSaldosAction) && !isOnline;

                                                        return (
                                                            <button
                                                                key={action.label}
                                                                type="button"
                                                                className={`resident-action ${action.variant} ${isSaldosAction ? 'action-saldos-btn' : ''} ${isHistoryAction ? 'action-historial-btn' : ''} ${isPaymentAction ? 'action-ticket-btn' : ''}`}
                                                                disabled={isDisabled}
                                                                title={isDisabled ? (isSaldosAction ? "Conexión requerida para ajustar saldos" : "Conexión requerida para registrar pago") : ""}
                                                                style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                                                onClick={() => {
                                                                    if (label.includes('historial')) { goToHistory(); return; }
                                                                    if (isPaymentAction) { goToRecibo(); return; }
                                                                    if (isSaldosAction) { handleOpenEditSaldosModal(resident); return; }
                                                                    if (label.includes('documentación') || label.includes('documentacion')) {
                                                                        navigate(`/administracion/departamentos/residentes/${statusType}/documentacion/${resident.id}`);
                                                                        return;
                                                                    }
                                                                }}
                                                            >
                                                                {ActionIcon ? <ActionIcon aria-hidden="true" /> : null}
                                                                <span>{action.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </footer>

                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Sección 'Recursos adicionales' eliminada por requerimiento */}
                {isPaymentModalOpen && paymentResident && (
                    <ModalRegistroPago
                        casa={paymentResident}
                        onClose={handleClosePaymentModal}
                        onConfirm={handleConfirmPago}
                    />
                )}
                {isHistoryModalOpen && historyResident && (
                    <div className="modal-overlay">
                        <div className="modal-content" style={{ maxWidth: '1400px', width: '95vw', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
                            <button
                                className="modal-close-btn"
                                onClick={handleCloseHistoryModal}
                                style={{
                                    position: 'absolute',
                                    top: '14px',
                                    right: '14px',
                                    background: '#ef4444',
                                    color: '#fff',
                                    border: 'none',
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '6px',
                                    fontSize: '1.3rem',
                                    fontWeight: '400',
                                    lineHeight: '1',
                                    cursor: 'pointer',
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = '#dc2626';
                                    e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.35)';
                                    e.target.style.transform = 'scale(1.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = '#ef4444';
                                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.25)';
                                    e.target.style.transform = 'scale(1)';
                                }}
                                onMouseDown={(e) => {
                                    e.target.style.transform = 'scale(0.96)';
                                }}
                                onMouseUp={(e) => {
                                    e.target.style.transform = 'scale(1.08)';
                                }}
                            >✕</button>
                            <div style={{ padding: '24px' }}>
                                <h2 style={{ marginTop: 0, color: '#1f2740' }}>Historial de Pagos - Casa {historyResident.numero}</h2>
                                <p style={{ color: '#666', marginBottom: '20px' }}>Residente: {historyResident.nombre_residente || historyResident.nombre || 'No registrado'}</p>

                                {sendingStatus && (
                                    <div style={{
                                        padding: '12px 16px',
                                        marginBottom: '16px',
                                        borderRadius: '8px',
                                        backgroundColor: sendingStatus === 'loading' ? '#e3f2fd' : sendingStatus.type === 'success' ? '#e8f5e9' : '#ffebee',
                                        color: sendingStatus === 'loading' ? '#1976d2' : sendingStatus.type === 'success' ? '#2e7d32' : '#c62828',
                                        fontSize: '0.9rem'
                                    }}>
                                        {sendingStatus === 'loading' ? '⏳ Procesando...' : (sendingStatus.type === 'success' ? '✓ ' : '✕ ') + (sendingStatus.msg || 'Operación completada')}
                                    </div>
                                )}

                                {/* Filtro de búsqueda y botón de exportación */}
                                <div style={{
                                    display: 'flex',
                                    gap: '12px',
                                    marginBottom: '20px',
                                    flexWrap: 'wrap',
                                    alignItems: 'center'
                                }}>
                                    <input
                                        type="text"
                                        placeholder="Buscar por concepto, referencia o método..."
                                        value={historySearchTerm}
                                        onChange={(e) => setHistorySearchTerm(e.target.value)}
                                        style={{
                                            flex: 1,
                                            minWidth: '250px',
                                            padding: '10px 14px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleExportToExcel}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '10px 16px',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        📊 Exportar a Excel
                                    </button>
                                </div>

                                {historyIngresos.length > 0 ? (
                                    <div style={{ overflowX: 'auto', maxHeight: '480px', overflowY: 'auto', position: 'relative', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'left', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Fecha</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'left', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Concepto</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'center', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Tipo</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'left', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Método de pago</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'left', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Referencia</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'right', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Cargo / Neto</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'right', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Abono / Pagado</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'center', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Email</th>
                                                    <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 10, textAlign: 'center', padding: '12px 8px', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', boxShadow: '0 1px 0 rgba(0,0,0,0.1)' }}>Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    // Filtrar y ordenar
                                                    const filtered = filteredHistory;
                                                    const totalPages = totalHistoryPages;
                                                    const currentPage = currentHistoryPage;
                                                    const paginated = filteredHistory.slice(currentPage * 9, currentPage * 9 + 9);
                                                    return paginated.flatMap((ingreso) => {
                                                         const dateObj = (ingreso.timestamp?.toDate ? ingreso.timestamp.toDate() : (ingreso.timestamp ? new Date(ingreso.timestamp) : null))
                                                             || (ingreso.fecha?.toDate ? ingreso.fecha.toDate() : new Date(ingreso.fecha || 0));
                                                         const dateLabel = dateObj ? dateObj.toLocaleDateString('es-MX') : 'N/A';
                                                         const timeLabel = dateObj ? dateObj.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';

                                                         // Lógica de Clasificación exacta
                                                         const conceptoRaw = ingreso.concepto || '';
                                                         const conceptoLower = (conceptoRaw || '').toLowerCase();
                                                         const tipoLower = (ingreso.tipo || '').toLowerCase();
                                                         const metodoLower = (ingreso.metodoPago || '').toLowerCase();
                                                         const refLower = (ingreso.referencia || '').toLowerCase();

                                                         const esImportado = refLower === 'historico_importado' || 
                                                                             ingreso.origen === 'importacion_historico' ||
                                                                             metodoLower.includes('importación');

                                                          let esAbonoMovimiento = false;
                                                          let isAdeudoRow = false;
                                                          const esTesoreriaMovimiento = !esPartidaContableResidente(ingreso);

                                                          if (esTesoreriaMovimiento) {
                                                              esAbonoMovimiento = false;
                                                          } else if (esImportado) {
                                                              if (tipoLower === 'pago' || tipoLower === 'descuento' || tipoLower === 'ajuste' || metodoLower.includes('importación histórica') || (ingreso.abono && ingreso.abono > 0)) {
                                                                  esAbonoMovimiento = true;
                                                              }
                                                          } else {
                                                              const esPagoPorMetodo = (metodoLower && metodoLower !== 'registro_automatico' && metodoLower !== 'n/a') &&
                                                                                      (metodoLower.includes('transferencia') || 
                                                                                       metodoLower.includes('bancaria') || 
                                                                                       metodoLower.includes('efectivo') || 
                                                                                       metodoLower.includes('tarjeta') || 
                                                                                       metodoLower.includes('importación'));

                                                              const esConceptoAbono = conceptoLower.includes('adeudos anteriores') || conceptoLower.includes('pago') || conceptoLower.includes('abono');

                                                              if (esPagoPorMetodo || (esConceptoAbono && metodoLower !== 'registro_automatico')) {
                                                                  esAbonoMovimiento = true;
                                                              } else {
                                                                  isAdeudoRow = tipoLower.includes('adeudo') || conceptoLower.includes('adeudo cuota') || conceptoLower.includes('adeudo historico') || conceptoLower.includes('adeudo extraordinaria');
                                                              }
                                                          }

                                                          const cuotaFallback = Number(financialConfig?.cuotaMensual || 375);
                                                          const valCargo = (esAbonoMovimiento || esTesoreriaMovimiento) ? 0 : (ingreso.cargo || ingreso.monto || ingreso.montoOriginal || ingreso.cuotaMensual || (isAdeudoRow ? cuotaFallback : 0));
                                                          const valAbono = esAbonoMovimiento ? (ingreso.abono || ingreso.monto || ingreso.montoNeto || 0) : (esTesoreriaMovimiento ? (ingreso.monto || ingreso.montoNeto || 0) : 0);

                                                         const esRegistroMensual = ingreso.esRegistroMensual === true;
                                                         const esAguaPotable = ingreso.tipo === 'Consumo de Agua Potable M3' || 
                                                             (ingreso.concepto && (ingreso.concepto.toLowerCase().includes('agua potable') || ingreso.concepto.toLowerCase().includes('cea')));
                                                         const isExtraordinaria = ingreso.tipo === 'extraordinaria' || 
                                                             (ingreso.referencia && (ingreso.referencia.includes('EXT-') || ingreso.referencia.includes('EXT'))) ||
                                                             (ingreso.concepto && (ingreso.concepto.toLowerCase().includes('extraordinaria') || ingreso.concepto.toLowerCase().includes('cuota extra')));

                                                         let estadoResidente = ingreso.estadoResidente || ingreso.estado || '';
                                                         let colorBandera = ingreso.colorBandera || 'verde';

                                                         if (esRegistroMensual) {
                                                             if (!ingreso.estadoResidente) {
                                                                 const esMoroso = (Number(ingreso.saldoFinal || 0) < 0);
                                                                 estadoResidente = esMoroso ? 'CON ADEUDO' : 'AL CORRIENTE';
                                                             }
                                                             if (!ingreso.colorBandera) {
                                                                 const esMoroso = (estadoResidente === 'CON ADEUDO') || (Number(ingreso.saldoFinal || 0) < 0);
                                                                 colorBandera = esMoroso ? 'rojo' : 'verde';
                                                             }
                                                         } else if (esAguaPotable) {
                                                             if (!estadoResidente) {
                                                                 const esMoroso = ingreso.estadoPago === 'moroso' || 
                                                                                  (Number(ingreso.montoNeto || ingreso.neto || 0) < 0) || 
                                                                                  (ingreso.concepto && ingreso.concepto.toLowerCase().includes('adeudo'));
                                                                 estadoResidente = esMoroso ? 'CON ADEUDO' : 'SIN ADEUDO';
                                                             }
                                                             if (!ingreso.colorBandera) {
                                                                 const esMoroso = estadoResidente === 'CON ADEUDO' || 
                                                                                  (Number(ingreso.montoNeto || ingreso.neto || 0) < 0);
                                                                 colorBandera = esMoroso ? 'rojo' : 'verde';
                                                             }
                                                         } else if (isExtraordinaria && !ingreso.estadoResidente) {
                                                             const esMoroso = ingreso.estadoPago === 'moroso' || 
                                                                              (Number(ingreso.montoNeto || 0) < 0) || 
                                                                              (ingreso.concepto && ingreso.concepto.toLowerCase().includes('adeudo'));
                                                             estadoResidente = esMoroso ? 'CON ADEUDO' : 'SIN ADEUDO';
                                                             colorBandera = esMoroso ? 'rojo' : 'verde';
                                                         }

                                                         const isSaldoFinal = conceptoLower.includes('saldo final de mes');
                                                         const mostrarBandera = esRegistroMensual || isSaldoFinal || isExtraordinaria || ingreso.tipo === 'interes' || esAguaPotable;

                                                         const displayConcept = isSaldoFinal ? 'SALDO FINAL DE MES' : ((conceptoLower.includes('adeudos anteriores')) ? (conceptoRaw || 'N/A') : (conceptoRaw || 'N/A'));

                                                         let referenciaDisplay = ingreso.referencia || 'N/A';
                                                         const tieneRefProfesional = /^(REG|EXT|INT)-/.test(referenciaDisplay) || referenciaDisplay.includes('Agua-Potable');

                                                         if (ingreso.esRegistroMensual && !tieneRefProfesional && dateObj) {
                                                             const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                                                             const yyyy = String(dateObj.getFullYear());
                                                             referenciaDisplay = 'CAR-' + mm + '-' + yyyy;
                                                         }

                                                         let tipoLabel = '';
                                                         try {
                                                             const mesesPermitidos = Number(historyResident?.mesesPermitidosGracia ?? financialConfig?.mesesPermitidos ?? financialConfig?.interesesMesesPermitidosDeuda ?? 1);
                                                             const mesesAdeudo = Number(historyResident?.mesesAdeudo ?? historyResident?.meses ?? 0);
                                                             const diaSanciones = Number(financialConfig?.sancionesInicioDia ?? financialConfig?.corteDia ?? 1);
                                                             const hoy = new Date();
                                                             const cumpleDias = hoy.getDate() >= diaSanciones;
                                                             const adeudoCasa = Number(historyResident?.adeudo ?? 0);
                                                             const condicionHistorico = (adeudoCasa > cuotaFallback * mesesPermitidos) && (mesesAdeudo >= mesesPermitidos) && cumpleDias && esRegistroMensual;
                                                             
                                                              if (esTesoreriaMovimiento) tipoLabel = '💼 Tesorería';
                                                              else if (isSaldoFinal) tipoLabel = 'Saldo';
                                                              else if (esAguaPotable) tipoLabel = ingreso.tipo || 'Consumo de Agua Potable M3';
                                                              else if (condicionHistorico) tipoLabel = 'Adeudo Historico';
                                                              else if (isAdeudoRow) tipoLabel = 'Adeudo Cuota';
                                                              else tipoLabel = 'Normal';
                                                          } catch (e) {
                                                              tipoLabel = esTesoreriaMovimiento ? '💼 Tesorería' : (isSaldoFinal ? 'Saldo' : (esAguaPotable ? (ingreso.tipo || 'Consumo de Agua Potable M3') : (isAdeudoRow ? 'Adeudo Cuota' : '')));
                                                         }

                                                         const rowBase = (
                                                             <tr key={ingreso.id + '-base'} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                                 <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                                                                     {dateLabel}
                                                                     {timeLabel ? (
                                                                         <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>{timeLabel}</div>
                                                                     ) : null}
                                                                 </td>
                                                                 <td style={{ padding: '12px', fontSize: '0.9rem', textAlign: 'left' }}>
                                                                     <div style={{ 
                                                                         fontWeight: (conceptoLower.includes('adeudos anteriores') || isSaldoFinal ? 800 : 400),
                                                                         color: isSaldoFinal ? (colorBandera === 'rojo' ? '#b91c1c' : '#15803d') : 'inherit'
                                                                     }}>{displayConcept}</div>
                                                                 </td>
                                                                 <td style={{ textAlign: 'center', padding: '12px', fontSize: '0.9rem' }}>
                                                                     <small style={{ color: '#374151', fontWeight: '500' }}>{tipoLabel}</small>
                                                                 </td>
                                                                 <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                                                                     {ingreso.metodoPago === 'saldo_favor' 
                                                                         ? 'Saldo a Favor' 
                                                                         : (ingreso.metodoPago === 'registro_automatico' 
                                                                             ? 'Registro Automático' 
                                                                             : (ingreso.metodoPago || 'N/A'))}
                                                                 </td>
                                                                 <td style={{ padding: '12px', fontSize: '0.9rem' }}>{referenciaDisplay}</td>
                                                                 
                                                                 {/* Cargo / Neto (Rojo) */}
                                                                 <td style={{ textAlign: 'right', padding: '12px', fontSize: '0.9rem', color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                     {valCargo > 0 ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(valCargo) : '-'}
                                                                 </td>
                                                                 
                                                                  {/* Abono / Pagado (Verde, Naranja o Ámbar Tesorería) */}
                                                                  <td style={{ textAlign: 'right', padding: '12px', fontSize: '0.9rem', color: esTesoreriaMovimiento ? '#b45309' : (esImportado ? '#ea580c' : '#16a34a'), fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                      {valAbono !== 0 ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(valAbono) : '-'}
                                                                  </td>
                                                                  
                                                                  <td style={{ textAlign: 'center', padding: '12px' }}>
                                                                      {ingreso.emailEnviado ? (
                                                                          <FaCheckCircle title="Correo enviado" style={{ color: '#27ae60', fontSize: '1.1rem' }} />
                                                                      ) : (
                                                                          <FaTimesCircle title="Correo no enviado" style={{ color: '#95a5a6', fontSize: '1.1rem' }} />
                                                                      )}
                                                                  </td>
                                                                  <td style={{ textAlign: 'center', padding: '12px' }}>
                                                                      {esTesoreriaMovimiento ? (
                                                                          <div style={{
                                                                              display: 'inline-flex',
                                                                              alignItems: 'center',
                                                                              justifyContent: 'center',
                                                                              padding: '6px 12px',
                                                                              borderRadius: '20px',
                                                                              fontSize: '0.8rem',
                                                                              fontWeight: '600',
                                                                              backgroundColor: '#fef3c7',
                                                                              color: '#b45309',
                                                                              border: '1px solid #fde68a'
                                                                          }} title="Ingreso de tesorería: no amortiza adeudo ni genera saldo a favor">
                                                                              💼 TESORERÍA
                                                                          </div>
                                                                      ) : esAbonoMovimiento ? (
                                                                          <div style={{
                                                                              display: 'inline-flex',
                                                                              alignItems: 'center',
                                                                              justifyContent: 'center',
                                                                              padding: '6px 12px',
                                                                              borderRadius: '20px',
                                                                              fontSize: '0.8rem',
                                                                              fontWeight: '600',
                                                                              backgroundColor: '#dcfce7',
                                                                              color: '#15803d',
                                                                              border: '1px solid #86efac'
                                                                          }}>
                                                                              ✅ PAGADO
                                                                          </div>
                                                                      ) : (valCargo > 0 || isAdeudoRow || esImportado || mostrarBandera) ? (
                                                                          <div style={{
                                                                              display: 'inline-flex',
                                                                              alignItems: 'center',
                                                                              justifyContent: 'center',
                                                                              padding: '6px 12px',
                                                                              borderRadius: '20px',
                                                                              fontSize: '0.8rem',
                                                                              fontWeight: '600',
                                                                              backgroundColor: (colorBandera === 'verde' && !isAdeudoRow && !esImportado) ? '#dcfce7' : '#fee2e2',
                                                                              color: (colorBandera === 'verde' && !isAdeudoRow && !esImportado) ? '#15803d' : '#991b1b',
                                                                              border: '1px solid ' + ((colorBandera === 'verde' && !isAdeudoRow && !esImportado) ? '#86efac' : '#fca5a5')
                                                                          }}>
                                                                              {(colorBandera === 'verde' && !isAdeudoRow && !esImportado) ? '✅ SIN ADEUDO' : '🚨 CON ADEUDO'}
                                                                          </div>
                                                                      ) : (
                                                                          <span style={{ color: '#999', fontSize: '0.9rem' }}>-</span>
                                                                      )}
                                                                  </td>
                                                             </tr>
                                                         );

                                                         const rows = [];

                                                         if (esRegistroMensual) {
                                                             const mm = dateObj ? String(dateObj.getMonth() + 1).padStart(2, '0') : String(new Date().getMonth() + 1).padStart(2, '0');
                                                             const yyyy = dateObj ? String(dateObj.getFullYear()) : String(new Date().getFullYear());
                                                             const nombreMes = dateObj ? dateObj.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }).toUpperCase() : '';

                                                             const esMarcadoSoloSaldo = ingreso.esSoloSaldoFinal || ingreso.esMarcadoHistorial || (ingreso.concepto && ingreso.concepto.startsWith('SALDO FINAL DE MES'));
                                                             const esMorosoCargo = colorBandera === 'rojo';
                                                             const finalSaldoMonto = esMorosoCargo
                                                                 ? Math.abs(ingreso.saldoFinal || ingreso.monto || 0)
                                                                 : (ingreso.desglose?.saldoFavorFinal || ingreso.saldoFavorFinal || 0);
                                                             const saldoFinalNeto = esMorosoCargo ? -finalSaldoMonto : finalSaldoMonto;
                                                             const estadoSaldoFinal = colorBandera === 'rojo' ? 'CON ADEUDO' : 'SIN ADEUDO';

                                                             const conceptoSaldoFinal = (esMarcadoSoloSaldo && ingreso.concepto)
                                                                 ? ingreso.concepto
                                                                 : 'SALDO FINAL DE MES';

                                                             const refSaldoFinal = ingreso.referencia || 'SAL-' + mm + '-' + yyyy;

                                                             const rowSaldoFinal = (
                                                                 <tr key={ingreso.id + '-saldo-final'} style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
                                                                     <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                                                                         {dateLabel}
                                                                         {timeLabel ? <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>{timeLabel}</div> : null}
                                                                     </td>
                                                                     <td style={{ padding: '12px', fontSize: '0.9rem', textAlign: 'left' }}>
                                                                         <div style={{ fontWeight: 800, color: colorBandera === 'rojo' ? '#b91c1c' : '#15803d' }}>
                                                                             {conceptoSaldoFinal}
                                                                         </div>
                                                                     </td>
                                                                     <td style={{ textAlign: 'center', padding: '12px', fontSize: '0.9rem' }}>
                                                                         <small style={{ color: '#374151', fontWeight: '500' }}>Saldo</small>
                                                                     </td>
                                                                     <td style={{ padding: '12px', fontSize: '0.9rem' }}>registro_automatico</td>
                                                                     <td style={{ padding: '12px', fontSize: '0.9rem' }}>{refSaldoFinal}</td>
                                                                     
                                                                     {/* Cargo / Neto (9 Columnas alineado) */}
                                                                     <td style={{ textAlign: 'right', padding: '12px', fontSize: '0.9rem', color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                         {(esMarcadoSoloSaldo && esMorosoCargo) ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(finalSaldoMonto) : '—'}
                                                                     </td>
                                                                     
                                                                     {/* Abono / Pagado (9 Columnas alineado) */}
                                                                     <td style={{ textAlign: 'right', padding: '12px', fontSize: '0.9rem', color: '#16a34a', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                         {(esMarcadoSoloSaldo && !esMorosoCargo) ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(finalSaldoMonto) : '—'}
                                                                     </td>
                                                                     
                                                                     <td style={{ textAlign: 'center', padding: '12px' }}>-</td>
                                                                     <td style={{ textAlign: 'center', padding: '12px' }}>
                                                                         <div style={{
                                                                             display: 'inline-flex',
                                                                             alignItems: 'center',
                                                                             justifyContent: 'center',
                                                                             padding: '6px 12px',
                                                                             borderRadius: '20px',
                                                                             fontSize: '0.8rem',
                                                                             fontWeight: '600',
                                                                             backgroundColor: colorBandera === 'rojo' ? '#fee2e2' : '#dcfce7',
                                                                             color: colorBandera === 'rojo' ? '#991b1b' : '#15803d',
                                                                             border: '1px solid ' + (colorBandera === 'rojo' ? '#fca5a5' : '#86efac')
                                                                         }}>
                                                                             {colorBandera === 'rojo' ? '🚨' : '✅'} {estadoSaldoFinal}
                                                                         </div>
                                                                     </td>
                                                                 </tr>
                                                             );
                                                             if (esMarcadoSoloSaldo) {
                                                                 rows.push(rowSaldoFinal);
                                                             } else {
                                                                 rows.push(rowBase);
                                                             }

                                                             if (ingreso.cuotasExtraniordinarias && ingreso.cuotasExtraniordinarias.length > 0) {
                                                                 // Filtrar cuotas extraordinarias repetidas dentro del mismo array por descripción normalizada
                                                                 const descripcionesVistas = new Set();
                                                                 const cuotasFiltradas = [];
                                                                 ingreso.cuotasExtraniordinarias.forEach(c => {
                                                                     const descNorm = c.descripcion.toLowerCase()
                                                                         .replace(/cuota\s+extra(ordinaria)?[:\s]*/gi, '')
                                                                         .replace(/\*/g, '')
                                                                         .replace(/[-_]/g, '')
                                                                         .trim();
                                                                     if (!descripcionesVistas.has(descNorm)) {
                                                                         descripcionesVistas.add(descNorm);
                                                                         cuotasFiltradas.push(c);
                                                                     }
                                                                 });

                                                                 cuotasFiltradas.forEach((cuotaExt, idx) => {
                                                                     const conceptoExtra = 'Adeudo Cuota Extraordinaria "' + cuotaExt.descripcion + '" ' + nombreMes;
                                                                     const referenciaExtra = 'EXT-' + mm + '-' + yyyy;
                                                                     const montoExtra = cuotaExt.monto || 0;

                                                                     const dateObjExtra = (cuotaExt.fechaCargo?.toDate ? cuotaExt.fechaCargo.toDate() : (cuotaExt.fechaCargo ? new Date(cuotaExt.fechaCargo) : null))
                                                                         || (cuotaExt.fechaCreacion?.toDate ? cuotaExt.fechaCreacion.toDate() : (cuotaExt.fechaCreacion ? new Date(cuotaExt.fechaCreacion) : null))
                                                                         || (cuotaExt.fecha?.toDate ? cuotaExt.fecha.toDate() : (cuotaExt.fecha ? new Date(cuotaExt.fecha) : null))
                                                                         || dateObj;
                                                                     const dateLabelExtra = dateObjExtra ? dateObjExtra.toLocaleDateString('es-MX') : dateLabel;
                                                                     const timeLabelExtra = dateObjExtra ? dateObjExtra.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : timeLabel;

                                                                     rows.push(
                                                                         <tr key={ingreso.id + '-ext-' + idx} style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#fdfdfd' }}>
                                                                             <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                                                                                 {dateLabelExtra}
                                                                                 {timeLabelExtra ? <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>{timeLabelExtra}</div> : null}
                                                                             </td>
                                                                             <td style={{ padding: '12px', fontSize: '0.9rem', textAlign: 'left' }}>
                                                                                 <div style={{ fontWeight: 500, color: '#3b82f6' }}>{conceptoExtra}</div>
                                                                             </td>
                                                                             <td style={{ textAlign: 'center', padding: '12px', fontSize: '0.9rem' }}>
                                                                                 <small style={{ color: '#374151', fontWeight: '500' }}>Adeudo Cuota Extraordinaria</small>
                                                                             </td>
                                                                             <td style={{ padding: '12px', fontSize: '0.9rem' }}>registro_automatico</td>
                                                                             <td style={{ padding: '12px', fontSize: '0.9rem' }}>{referenciaExtra}</td>
                                                                             
                                                                             <td style={{ textAlign: 'right', padding: '12px', fontSize: '0.9rem', color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                                 {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(montoExtra)}
                                                                             </td>
                                                                             <td style={{ textAlign: 'right', padding: '12px', fontSize: '0.9rem', color: '#888', whiteSpace: 'nowrap' }}>
                                                                                 —
                                                                             </td>
                                                                             
                                                                             <td style={{ textAlign: 'center', padding: '12px' }}>-</td>
                                                                             <td style={{ textAlign: 'center', padding: '12px' }}>
                                                                                 <div style={{
                                                                                     display: 'inline-flex',
                                                                                     alignItems: 'center',
                                                                                     justifyContent: 'center',
                                                                                     padding: '6px 12px',
                                                                                     borderRadius: '20px',
                                                                                     fontSize: '0.8rem',
                                                                                     fontWeight: '600',
                                                                                     backgroundColor: '#fee2e2',
                                                                                     color: '#991b1b',
                                                                                     border: '1px solid #fca5a5'
                                                                                 }}>
                                                                                     🚨 CON ADEUDO
                                                                                 </div>
                                                                             </td>
                                                                         </tr>
                                                                     );
                                                                 });
                                                             }
                                                         } else {
                                                             rows.push(rowBase);
                                                         }

                                                         return rows;
                                                     })
                                                 })()}
                                             </tbody>
                                            {(() => {
                                                const listadoExpandido = [];
                                                
                                                historyIngresos.forEach(ingreso => {
                                                    const conceptoLower = (ingreso.concepto || '').toLowerCase();
                                                    const tipoLower = (ingreso.tipo || '').toLowerCase();
                                                    const metodoLower = (ingreso.metodoPago || '').toLowerCase();
                                                    const refLower = (ingreso.referencia || '').toLowerCase();
                                                    
                                                    const esImportado = refLower === 'historico_importado' || 
                                                                        ingreso.origen === 'importacion_historico' ||
                                                                        metodoLower.includes('importación');
                                                    
                                                    let esAbonoMovimiento = false;
                                                    let isAdeudoRow = false;
                                                    const esTesoreriaMovimiento = !esPartidaContableResidente(ingreso);
                                                    
                                                    if (esTesoreriaMovimiento) {
                                                        esAbonoMovimiento = false;
                                                    } else if (esImportado) {
                                                        if (tipoLower === 'pago' || metodoLower.includes('importación histórica')) {
                                                            esAbonoMovimiento = true;
                                                        }
                                                    } else {
                                                        const esPagoPorMetodo = (metodoLower && metodoLower !== 'registro_automatico' && metodoLower !== 'n/a') &&
                                                                                (metodoLower.includes('transferencia') || 
                                                                                 metodoLower.includes('bancaria') || 
                                                                                 metodoLower.includes('efectivo') || 
                                                                                 metodoLower.includes('tarjeta') || 
                                                                                 metodoLower.includes('importación'));
                                                        
                                                        const esConceptoAbono = conceptoLower.includes('adeudos anteriores') || conceptoLower.includes('pago') || conceptoLower.includes('abono');
                                                        
                                                        if (esPagoPorMetodo || (esConceptoAbono && metodoLower !== 'registro_automatico')) {
                                                            esAbonoMovimiento = true;
                                                        } else {
                                                            isAdeudoRow = tipoLower.includes('adeudo') || conceptoLower.includes('adeudo cuota') || conceptoLower.includes('adeudo historico') || conceptoLower.includes('adeudo extraordinaria');
                                                        }
                                                    }
                                                    
                                                    const cuotaFallback = Number(financialConfig?.cuotaMensual || 375);
                                                    const valCargo = (esAbonoMovimiento || esTesoreriaMovimiento) ? 0 : (ingreso.cargo || ingreso.monto || ingreso.montoOriginal || ingreso.cuotaMensual || (isAdeudoRow ? cuotaFallback : 0));
                                                    const valAbono = esAbonoMovimiento ? (ingreso.abono || ingreso.monto || ingreso.montoNeto || 0) : 0;
                                                    
                                                    const isSaldoFinal = conceptoLower.includes('saldo final de mes');
                                                    
                                                    if (!isSaldoFinal || valCargo > 0) {
                                                        listadoExpandido.push({
                                                            concepto: ingreso.concepto || 'N/A',
                                                            cargo: esTesoreriaMovimiento ? 0 : valCargo,
                                                            abono: (isSaldoFinal || esTesoreriaMovimiento) ? 0 : valAbono,
                                                            referencia: ingreso.referencia || 'N/A'
                                                        });
                                                    }
                                                    
                                                    if (!ingreso.esRegistroMensual && ingreso.cuotasExtraniordinarias && ingreso.cuotasExtraniordinarias.length > 0) {
                                                        // Filtrar cuotas extraordinarias repetidas dentro del mismo array por descripción normalizada
                                                        const descripcionesVistas = new Set();
                                                        const cuotasFiltradas = [];
                                                        ingreso.cuotasExtraniordinarias.forEach(c => {
                                                            const descNorm = c.descripcion.toLowerCase()
                                                                .replace(/cuota\s+extra(ordinaria)?[:\s]*/gi, '')
                                                                .replace(/\*/g, '')
                                                                .replace(/[-_]/g, '')
                                                                .trim();
                                                            if (!descripcionesVistas.has(descNorm)) {
                                                                descripcionesVistas.add(descNorm);
                                                                cuotasFiltradas.push(c);
                                                            }
                                                        });

                                                        cuotasFiltradas.forEach(cuotaExt => {
                                                            listadoExpandido.push({
                                                                concepto: 'Adeudo Cuota Extraordinaria ' + cuotaExt.descripcion,
                                                                cargo: cuotaExt.monto || 0,
                                                                abono: 0,
                                                                referencia: 'EXT-' + (ingreso.referencia || 'N/A')
                                                            });
                                                        });
                                                    }
                                                });
                                                
                                                // Imprimir logs agrupados de cargos y abonos de forma consecutiva (usando $$' para escapar el $ y evitar el bug de RegExp.replace)
                                                if (historyResident?.id) {
                                                    console.log('=== [Historial] Iniciando procesamiento de totales para:', historyResident.id);
                                                    
                                                    console.log('--- SECCIÓN DE CARGOS ---');
                                                    let sumaCargos = 0;
                                                    listadoExpandido.forEach(item => {
                                                        if (item.cargo > 0) {
                                                            sumaCargos += item.cargo;
                                                            console.log('[Historial Cargo] Concepto: ' + item.concepto + ', Monto: $$' + item.cargo + ', Ref: ' + item.referencia);
                                                        }
                                                    });
                                                    console.log('-> Suma Parcial de Cargos: $$' + sumaCargos);
                                                    
                                                    console.log('--- SECCIÓN DE ABONOS ---');
                                                    let sumaAbonos = 0;
                                                    listadoExpandido.forEach(item => {
                                                        if (item.abono > 0) {
                                                            sumaAbonos += item.abono;
                                                            console.log('[Historial Abono] Concepto: ' + item.concepto + ', Monto: $$' + item.abono + ', Ref: ' + item.referencia);
                                                        }
                                                    });
                                                    console.log('-> Suma Parcial de Abonos: $$' + sumaAbonos);
                                                    
                                                    const pendienteFinal = sumaCargos - sumaAbonos;
                                                    if (pendienteFinal >= 0) {
                                                        console.log('=== [Historial Totales] Pendiente Final: $$' + pendienteFinal);
                                                    } else {
                                                        console.log('=== [Historial Totales] Saldo a Favor Final: $$' + Math.abs(pendienteFinal));
                                                    }
                                                }
                                                
                                                const totalCargosHist = listadoExpandido.reduce((sum, x) => sum + Number(x.cargo || 0), 0);
                                                const totalAbonosHist = listadoExpandido.reduce((sum, x) => sum + Number(x.abono || 0), 0);
                                                const totalPendienteHist = totalCargosHist - totalAbonosHist;

                                                return (
                                                    <tfoot style={{ position: 'sticky', bottom: 0, backgroundColor: '#f8fafc', zIndex: 10, boxShadow: '0 -2px 5px rgba(0,0,0,0.05)', borderTop: '2px solid #cbd5e1' }}>
                                                        <tr style={{ fontWeight: 'bold' }}>
                                                            <td colSpan="5" style={{ padding: '12px', fontSize: '0.95rem', color: '#1e293b', textAlign: 'right' }}>TOTALES ACUMULADOS:</td>
                                                            <td style={{ padding: '12px', fontSize: '0.95rem', color: '#dc2626', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalCargosHist)}
                                                            </td>
                                                            <td style={{ padding: '12px', fontSize: '0.95rem', color: '#16a34a', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalAbonosHist)}
                                                            </td>
                                                            <td colSpan="2" style={{ padding: '12px', fontSize: '0.95rem', color: totalPendienteHist > 0 ? '#dc2626' : '#16a34a', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                {totalPendienteHist < 0
                                                                    ? `Saldo a Favor: ${new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Math.abs(totalPendienteHist))}`
                                                                    : `Pendiente: ${new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalPendienteHist)}`}
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                );
                                            })()}
                                        </table>
                                        {/* Paginación */}
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
                                             <button onClick={() => setHistoryPage(Math.max(currentHistoryPage - 1, 0))} disabled={currentHistoryPage <= 0} style={{ padding: '6px 10px' }}>Anterior</button>
                                             <div style={{ padding: '6px 10px', color: '#555', fontWeight: '500' }}>Página {currentHistoryPage + 1} de {totalHistoryPages}</div>
                                             <button onClick={() => setHistoryPage(currentHistoryPage + 1)} disabled={currentHistoryPage >= totalHistoryPages - 1} style={{ padding: '6px 10px' }}>Siguiente</button>
                                         </div>
                                    </div>
                                ) : (
                                    <p style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>No hay pagos registrados para esta casa.</p>
                                )}

                                {selectedIngreso && (
                                    <div style={{
                                        marginTop: '24px',
                                        padding: '16px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '8px',
                                        borderLeft: '4px solid #5c6cff'
                                    }}>
                                        <p style={{ margin: '0 0 12px 0', color: '#1f2740', fontWeight: '600' }}>Opciones para: {selectedIngreso.concepto || 'N/A'}</p>
                                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                            <button
                                                type="button"
                                                onClick={() => handleDownloadReceipt(selectedIngreso)}
                                                disabled={sendingStatus === 'loading'}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '10px 16px',
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    border: '1px solid #1976d2',
                                                    borderRadius: '6px',
                                                    cursor: sendingStatus === 'loading' ? 'not-allowed' : 'pointer',
                                                    fontWeight: '500',
                                                    opacity: sendingStatus === 'loading' ? 0.6 : 1
                                                }}
                                            >
                                                <FaDownload /> Descargar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSendByEmail(selectedIngreso)}
                                                disabled={sendingStatus === 'loading'}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '10px 16px',
                                                    backgroundColor: '#e8f5e9',
                                                    color: '#2e7d32',
                                                    border: '1px solid #2e7d32',
                                                    borderRadius: '6px',
                                                    cursor: sendingStatus === 'loading' ? 'not-allowed' : 'pointer',
                                                    fontWeight: '500',
                                                    opacity: sendingStatus === 'loading' ? 0.6 : 1
                                                }}
                                            >
                                                <FaEnvelopeOpen /> Enviar por Correo
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {selectedResident && (
                    <ResidentProfileModal
                        open={true}
                        resident={selectedResident}
                        onClose={() => setSelectedResident(null)}
                    />
                )}

                {showSaldosModal && saldosCasaData && (
                    <ModalEdicionSaldos
                        isOpen={showSaldosModal}
                        casa={saldosCasaData}
                        fraccionamientoId={fraccionamientoId}
                        userData={userData}
                        onClose={() => setShowSaldosModal(false)}
                        onSuccess={() => setShowSaldosModal(false)}
                    />
                )}

                <ConfirmationModal
                    open={confirmationModal.open}
                    title={confirmationModal.title}
                    message={confirmationModal.message}
                    type={confirmationModal.type}
                    icon={confirmationModal.type === 'success' ? <FaCheckCircle /> : undefined}
                    actionLabel="Aceptar"
                    onClose={() => {
                        setConfirmationModal({ ...confirmationModal, open: false });
                        if (confirmationModal.type === 'success') {
                            handleClosePaymentModal();
                            window.location.reload();
                        }
                    }}
                    onAction={() => {
                        if (confirmationModal.type === 'success') {
                            handleClosePaymentModal();
                            window.location.reload();
                        }
                    }}
                />

                {/* Modal / Bottom Sheet Zen: Desglose Detallado de Adeudos (Vista Administrador) */}
                {desgloseModalData.isOpen && (
                    <div
                        className="modal-desglose-overlay"
                        onClick={() => setDesgloseModalData(prev => ({ ...prev, isOpen: false }))}
                    >
                        <div className="modal-desglose-sheet" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-desglose-drag-handle" />

                            {/* Header del Modal */}
                            <div className="modal-desglose-header">
                                <div className="modal-desglose-title-box">
                                    <div className={`modal-desglose-icon ${desgloseModalData.tab}`}>
                                        {desgloseModalData.tab === 'mantenimiento' ? <FaDollarSign /> : desgloseModalData.tab === 'extraordinarias' ? <FaCalendarAlt /> : <FaGavel />}
                                    </div>
                                    <div>
                                        <h3 className="modal-desglose-title">Desglose Detallado de Adeudos</h3>
                                        <p className="modal-desglose-subtitle">
                                            Casa {desgloseModalData.resident?.numero || desgloseModalData.resident?.numeroCasa || desgloseModalData.resident?.casaLabel || ''} • {desgloseModalData.resident?.nombre || desgloseModalData.resident?.nombre_residente || desgloseModalData.resident?.residentName || 'Residente'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="modal-desglose-close-btn"
                                    onClick={() => setDesgloseModalData(prev => ({ ...prev, isOpen: false }))}
                                    title="Cerrar ventana"
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            {/* Pestañas Zen (Tabs) */}
                            <div className="modal-desglose-tabs">
                                <button
                                    type="button"
                                    className={`modal-desglose-tab ${desgloseModalData.tab === 'mantenimiento' ? 'active' : ''}`}
                                    onClick={() => setDesgloseModalData(prev => ({ ...prev, tab: 'mantenimiento' }))}
                                >
                                    <FaDollarSign className="tab-icon" />
                                    <span>
                                        Mantenimiento ({desgloseModalData.loading ? '...' : `${desgloseMantenimientoAgrupado.reduce((acc, g) => acc + g.meses.length, 0)} meses`})
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className={`modal-desglose-tab ${desgloseModalData.tab === 'extraordinarias' ? 'active' : ''}`}
                                    onClick={() => setDesgloseModalData(prev => ({ ...prev, tab: 'extraordinarias' }))}
                                >
                                    <FaCalendarAlt className="tab-icon" />
                                    <span>
                                        Extraordinarias ({desgloseModalData.loading ? '...' : `${desgloseCuotasExtrasDetalladas.length} cuotas`})
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className={`modal-desglose-tab ${desgloseModalData.tab === 'sanciones' ? 'active' : ''}`}
                                    onClick={() => setDesgloseModalData(prev => ({ ...prev, tab: 'sanciones' }))}
                                >
                                    <FaGavel className="tab-icon" />
                                    <span>
                                        Sanciones ({desgloseModalData.loading ? '...' : `${(desgloseModalData.multasData || []).length} sanciones`})
                                    </span>
                                </button>
                            </div>

                            {/* Cuerpo con Scroll Suave */}
                            <div className="modal-desglose-body">
                                {desgloseModalData.loading ? (
                                    <div className="desglose-empty-state">
                                        <div className="spinner-border" style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />
                                        <p style={{ marginTop: '12px', fontWeight: 600 }}>Consultando subcolección de adeudos en tiempo real...</p>
                                    </div>
                                ) : desgloseModalData.tab === 'mantenimiento' ? (
                                    <div className="desglose-content-pane">
                                        {/* Resumen Zen */}
                                        {(() => {
                                            const totalMantenimientoCalculado = desgloseMantenimientoAgrupado.reduce((sum, g) => sum + g.totalMontoPendiente, 0);
                                            const totalMeses = desgloseMantenimientoAgrupado.reduce((sum, g) => sum + g.meses.length, 0);
                                            return (
                                                <div className="desglose-resumen-banner mantto">
                                                    <div className="resumen-metric">
                                                        <span className="resumen-label">Total Adeudo Mantenimiento</span>
                                                        <span className="resumen-val">{currencyFormatter.format(totalMantenimientoCalculado)}</span>
                                                    </div>
                                                    <div className="resumen-divider" />
                                                    <div className="resumen-metric">
                                                        <span className="resumen-label">Meses Pendientes</span>
                                                        <span className="resumen-val-secondary">{totalMeses} meses</span>
                                                    </div>
                                                    <div className="resumen-divider" />
                                                    <div className="resumen-metric">
                                                        <span className="resumen-label">Años con Adeudo</span>
                                                        <span className="resumen-val-secondary">{desgloseMantenimientoAgrupado.length} años</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Acciones de colapso rápido */}
                                        {desgloseMantenimientoAgrupado.length > 1 && (
                                            <div className="desglose-quick-actions">
                                                <span className="quick-actions-hint">Presiona un año para expandir o colapsar sus meses:</span>
                                                <div className="quick-actions-btns">
                                                    <button
                                                        type="button"
                                                        className="btn-quick-text"
                                                        onClick={() => {
                                                            const expandAll = {};
                                                            desgloseMantenimientoAgrupado.forEach(g => expandAll[g.anio] = true);
                                                            setDesgloseModalData(prev => ({ ...prev, aniosExpandidos: expandAll }));
                                                        }}
                                                    >
                                                        Expandir todos
                                                    </button>
                                                    <span>•</span>
                                                    <button
                                                        type="button"
                                                        className="btn-quick-text"
                                                        onClick={() => setDesgloseModalData(prev => ({ ...prev, aniosExpandidos: {} }))}
                                                    >
                                                        Colapsar todos
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Lista de Años y Meses */}
                                        {desgloseMantenimientoAgrupado.length === 0 ? (
                                            <div className="desglose-empty-state">
                                                <FaInfoCircle className="empty-icon" />
                                                <p>No se registran meses pendientes de mantenimiento para esta vivienda.</p>
                                            </div>
                                        ) : (
                                            <div className="desglose-anios-accordion">
                                                {desgloseMantenimientoAgrupado.map((grupo) => {
                                                    const estaAbierto = desgloseModalData.aniosExpandidos[grupo.anio] !== false; // Abierto por defecto
                                                    return (
                                                        <div key={grupo.anio} className="anio-accordion-card">
                                                            <div
                                                                className="anio-accordion-header"
                                                                onClick={() => setDesgloseModalData(prev => ({
                                                                    ...prev,
                                                                    aniosExpandidos: {
                                                                        ...prev.aniosExpandidos,
                                                                        [grupo.anio]: !estaAbierto
                                                                    }
                                                                }))}
                                                            >
                                                                <div className="anio-header-left">
                                                                    <span className="anio-pill">Año {grupo.anio}</span>
                                                                    <span className="anio-meses-count">
                                                                        {grupo.meses.length} {grupo.meses.length === 1 ? 'mes' : 'meses'} pendientes
                                                                    </span>
                                                                </div>
                                                                <div className="anio-header-right">
                                                                    <span className="anio-monto-total">{currencyFormatter.format(grupo.totalMontoPendiente)}</span>
                                                                    <span className={`anio-arrow-icon ${estaAbierto ? 'open' : ''}`}>
                                                                        <FaChevronDown />
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {estaAbierto && (
                                                                <div className="anio-accordion-content">
                                                                    <div className="meses-grid-list">
                                                                        {grupo.meses.map((mesItem) => {
                                                                            const tieneAbono = mesItem.montoPagado > 0;
                                                                            return (
                                                                                <div key={mesItem.id} className="mes-card-item">
                                                                                    <div className="mes-card-left">
                                                                                        <div className="mes-name-box">
                                                                                            <span className="mes-name-text">{mesItem.mesNombre} {grupo.anio}</span>
                                                                                            {tieneAbono && (
                                                                                                <span className="badge-abono-parcial">
                                                                                                    Abono de {currencyFormatter.format(mesItem.montoPagado)}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <span className="mes-original-hint">
                                                                                            Cuota fijada: {currencyFormatter.format(mesItem.montoOriginal)}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="mes-card-right">
                                                                                        <span className="mes-monto-pendiente">
                                                                                            {currencyFormatter.format(mesItem.montoPendiente)}
                                                                                        </span>
                                                                                        <span className={`mes-status-tag ${mesItem.estado === 'pagado_parcialmente' ? 'parcial' : 'pendiente'}`}>
                                                                                            {mesItem.estado === 'pagado_parcialmente' ? 'Abono Parcial' : 'Pendiente'}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ) : desgloseModalData.tab === 'extraordinarias' ? (
                                    <div className="desglose-content-pane">
                                        {/* Resumen Zen Extraordinarias */}
                                        {(() => {
                                            const totalExtrasCalculado = desgloseCuotasExtrasDetalladas.reduce((sum, c) => sum + c.montoPendiente, 0);
                                            return (
                                                <div className="desglose-resumen-banner extras">
                                                    <div className="resumen-metric">
                                                        <span className="resumen-label">Total Cuotas Extraordinarias</span>
                                                        <span className="resumen-val">{currencyFormatter.format(totalExtrasCalculado)}</span>
                                                    </div>
                                                    <div className="resumen-divider" />
                                                    <div className="resumen-metric">
                                                        <span className="resumen-label">Cuotas Pendientes</span>
                                                        <span className="resumen-val-secondary">{desgloseCuotasExtrasDetalladas.length} cuotas</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {desgloseCuotasExtrasDetalladas.length === 0 ? (
                                            <div className="desglose-empty-state">
                                                <FaInfoCircle className="empty-icon" />
                                                <p>No se registran cuotas extraordinarias pendientes para esta vivienda.</p>
                                            </div>
                                        ) : (
                                            <div className="cuotas-extras-detailed-list">
                                                {desgloseCuotasExtrasDetalladas.map((cuota, idx) => {
                                                    const tieneAbono = cuota.montoPagado > 0;
                                                    return (
                                                        <div key={cuota.id || idx} className="cuota-extra-detailed-card">
                                                            <div className="cuota-extra-top">
                                                                <div className="cuota-extra-info">
                                                                    <span className="cuota-extra-badge-idx">{idx + 1}</span>
                                                                    <div>
                                                                        <h4 className="cuota-extra-title">{cuota.concepto}</h4>
                                                                        {cuota.anio && (
                                                                            <span className="cuota-extra-periodo">Periodo anual: {cuota.anio}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="cuota-extra-amounts">
                                                                    <span className="cuota-extra-pending-amount">
                                                                        {currencyFormatter.format(cuota.montoPendiente)}
                                                                    </span>
                                                                    <span className={`cuota-extra-status-pill ${cuota.estado === 'pagado_parcial' ? 'parcial' : 'pendiente'}`}>
                                                                        {cuota.estado === 'pagado_parcial' ? 'Abono Parcial' : 'Pendiente'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="cuota-extra-bottom">
                                                                <span className="cuota-extra-detail-meta">
                                                                    Monto de la cuota: <strong>{currencyFormatter.format(cuota.montoOriginal)}</strong>
                                                                </span>
                                                                {tieneAbono && (
                                                                    <span className="cuota-extra-detail-meta abono">
                                                                        Abonado a la fecha: <strong>{currencyFormatter.format(cuota.montoPagado)}</strong>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="desglose-content-pane">
                                        {/* Resumen Zen Sanciones Administrativas */}
                                        {(() => {
                                            const multasList = desgloseModalData.multasData || [];
                                            const totalSancionesCalculado = multasList.reduce((sum, m) => sum + Number(m.monto || 0), 0);
                                            return (
                                                <div className="desglose-resumen-banner sanciones">
                                                    <div className="resumen-metric">
                                                        <span className="resumen-label">Total Sanciones Administrativas</span>
                                                        <span className="resumen-val">{currencyFormatter.format(totalSancionesCalculado)}</span>
                                                    </div>
                                                    <div className="resumen-divider" />
                                                    <div className="resumen-metric">
                                                        <span className="resumen-label">Sanciones Activas</span>
                                                        <span className="resumen-val-secondary">{multasList.length} sanciones</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {(!desgloseModalData.multasData || desgloseModalData.multasData.length === 0) ? (
                                            <div className="desglose-empty-state">
                                                <FaInfoCircle className="empty-icon" />
                                                <p>No se registran sanciones administrativas pendientes para esta vivienda.</p>
                                            </div>
                                        ) : (
                                            <div className="cuotas-extras-detailed-list">
                                                {desgloseModalData.multasData.map((multa, idx) => {
                                                    const fechaFalta = multa.fechaFalta?.toDate 
                                                        ? multa.fechaFalta.toDate() 
                                                        : (multa.fechaFalta ? new Date(multa.fechaFalta) : (multa.fechaCaptura?.toDate ? multa.fechaCaptura.toDate() : new Date()));
                                                    return (
                                                        <div key={multa.id || idx} className="cuota-extra-detailed-card" style={{ borderLeft: '4px solid #ec4899' }}>
                                                            <div className="cuota-extra-top">
                                                                <div className="cuota-extra-info">
                                                                    <span className="cuota-extra-badge-idx" style={{ background: '#fdf2f8', color: '#db2777', borderColor: '#fbcfe8' }}>{idx + 1}</span>
                                                                    <div>
                                                                        <h4 className="cuota-extra-title">{multa.multaDescripcion || 'Sanción Administrativa'}</h4>
                                                                        <span className="cuota-extra-periodo">
                                                                            Fecha de infracción: {fechaFalta.toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City' })}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="cuota-extra-amounts">
                                                                    <span className="cuota-extra-pending-amount" style={{ color: '#db2777' }}>
                                                                        {currencyFormatter.format(Number(multa.monto || 0))}
                                                                    </span>
                                                                    <span className="cuota-extra-status-pill pendiente" style={{ background: '#fdf2f8', color: '#db2777', borderColor: '#fbcfe8' }}>
                                                                        Pendiente
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="cuota-extra-bottom">
                                                                {multa.comentarios && (
                                                                    <span className="cuota-extra-detail-meta">
                                                                        Observaciones: <strong>{multa.comentarios}</strong>
                                                                    </span>
                                                                )}
                                                                {multa.actorNombre && (
                                                                    <span className="cuota-extra-detail-meta" style={{ marginLeft: multa.comentarios ? '12px' : 0 }}>
                                                                        Registrado por: <strong>{multa.actorNombre}</strong>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Footer Zen */}
                            <div className="modal-desglose-footer">
                                <button
                                    type="button"
                                    className="btn-modal-desglose-cerrar"
                                    onClick={() => setDesgloseModalData(prev => ({ ...prev, isOpen: false }))}
                                >
                                    Entendido / Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResidentStatusPage;
